"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "../../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Badge } from "../../ui/badge"
import { Input } from "../../ui/input"
import { Textarea } from "../../ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"
import { Clock, Calendar, Plus, Trash2, Info, ChevronDown, Send, X, MessageSquare, ChevronRight, Loader2 } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover"
import { Switch } from "../../ui/switch"
import { BulkEntryModal } from "./bulk-entry-modal"
import { WeekSelector } from "./week-selector"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog"
import { CommentsModalEmp } from "../../modals/comments-emp"
import { toast } from "../../hooks/use-toast"
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip"
import { decodeHtmlEntities } from "../../lib/utils"


interface TimesheetPageProps {
  sidebarOpen?: boolean
}

export function TimesheetPage({ sidebarOpen = false }: TimesheetPageProps) {
  // UI State
  const [showNag1, setShowNag1] = useState(true)
  const [showPopup, setShowPopup] = useState(false)
  const [currentWeek, setCurrentWeek] = useState("")
  
  /**
   * Checks if the currently viewed week matches the current timesheet week from v1 API.
   * Used to enable/disable navigation buttons and copy previous week functionality.
   * @returns {boolean} True if viewing current TS week, false otherwise
   */
  const isViewingCurrentTSWeek = () => {
    if (!currentWeek || !v1CurrentWeekStartDate || !v1CurrentWeekEndDate) return false
    try {
      const [range, yearStr] = currentWeek.split(", ")
      const [startStr] = range.split(" - ")
      const monthsAbbr: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
      const [monAbbr, dayStr] = startStr.split(" ")
      const monday = new Date(Number.parseInt(yearStr), monthsAbbr[monAbbr], Number.parseInt(dayStr))
      return checkIfWeekMatchesCurrentWeek(monday)
    } catch {
      return false
    }
  }
  const [selectedDate, setSelectedDate] = useState("Monday, August 18")
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false)
  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false)
  const [weekDays, setWeekDays] = useState([
    { day: "Mon", date: "18", full: "Monday, August 18", status: "filled", isWeekend: false, iso: "" },
    { day: "Tue", date: "19", full: "Tuesday, August 19", status: "filled", isWeekend: false, iso: "" },
    { day: "Wed", date: "20", full: "Wednesday, August 20", status: "filled", isWeekend: false, iso: "" },
    { day: "Thu", date: "21", full: "Thursday, August 21", status: "filled", isWeekend: false, iso: "" },
    { day: "Fri", date: "22", full: "Friday, August 22", status: "filled", isWeekend: false, iso: "" },
    { day: "Sat", date: "23", full: "Saturday, August 23", status: "optional", isWeekend: true, iso: "" },
    { day: "Sun", date: "24", full: "Sunday, August 24", status: "optional", isWeekend: true, iso: "" },
  ])


  const [engagementOptions, setEngagementOptions] = useState<{ engagementID: number; title: string; startDate?: string; endDate?: string }[]>([])
  const [taskOptions, setTaskOptions] = useState<{ taskID: number; taskName: string }[]>([])

  const [timesheetId, setTimesheetId] = useState<number | null>(null)
  const [isSubmitOpen, setIsSubmitOpen] = useState(false)
  const [submitComment, setSubmitComment] = useState("")
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>("Timesheet Submitted Successfully")
  const [warnMessage, setWarnMessage] = useState<string>("")
  const [showWarnToast, setShowWarnToast] = useState(false)
  const [isViewingSpecificWeek, setIsViewingSpecificWeek] = useState(false)
  const [selectedWeekTimesheetId, setSelectedWeekTimesheetId] = useState<number | null>(null)
  const [timesheetStatus, setTimesheetStatus] = useState<number | null>(null)
  const [isGoToCurrentTSDisabled, setIsGoToCurrentTSDisabled] = useState(false)

  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [commentsData, setCommentsData] = useState<any[]>([])

  const [v1CurrentStartISO, setV1CurrentStartISO] = useState<string | null>(null)
  const [v1CurrentEndISO, setV1CurrentEndISO] = useState<string | null>(null)
  const [v1CurrentWeekStartDate, setV1CurrentWeekStartDate] = useState<Date | null>(null)
  const [v1CurrentWeekEndDate, setV1CurrentWeekEndDate] = useState<Date | null>(null)
  const [firstTimesheetStartDate, setFirstTimesheetStartDate] = useState<Date | null>(null)
  const [firstTimesheetStartISO, setFirstTimesheetStartISO] = useState<string | null>(null)
  const [rejectedCount, setRejectedCount] = useState<number>(0)
  const [rejectedRanges, setRejectedRanges] = useState<{ timesheetID: number; startDate: string; endDate: string }[]>([])

  // New state for weekly table view
  interface WeeklyTaskEntry {
    id: string
    engagementID: number
    engagementName: string
    taskID: number
    taskName: string
    times: { [dayKey: string]: { hours: number; minutes: number; comment: string; lineID?: number; duplicateLineIDs?: number[] } }
  }

  const [weeklyEntries, setWeeklyEntries] = useState<WeeklyTaskEntry[]>([])
  const [selectedEngagements, setSelectedEngagements] = useState<Set<number>>(new Set()) // Track selected engagements
  const [expandedEngagements, setExpandedEngagements] = useState<Set<number>>(new Set())
  const [commentPopovers, setCommentPopovers] = useState<{ [key: string]: boolean }>({})
  const [commentTexts, setCommentTexts] = useState<{ [key: string]: string }>({})
  const [failedCells, setFailedCells] = useState<Set<string>>(new Set()) // Track cells with failed saves
  const [focusedCellValues, setFocusedCellValues] = useState<{ [key: string]: { hours: number; minutes: number } }>({}) // Track initial values when cell is focused
  const [showWeekend, setShowWeekend] = useState(false)
  const [addingEngagementId, setAddingEngagementId] = useState<number | null>(null)
  const [addingTaskId, setAddingTaskId] = useState<string | null>(null)
  const [taskSelectOpen, setTaskSelectOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ type: 'engagement' | 'task', id: string | number, name: string, engagementID?: number, taskID?: number } | null>(null)
  // Loading and operation states
  // Autosave coordination:
  // - debounce per-cell (so editing one cell doesn't cancel saves for another)
  // - prevent concurrent saves per-cell (avoids duplicate inserts when lineID is null)
  // - read latest state at save-time (avoids stale closures)
  const autosaveTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const savingCellsRef = useRef<Set<string>>(new Set())
  const pendingAutosavesRef = useRef<Set<string>>(new Set())
  const weeklyEntriesRef = useRef<WeeklyTaskEntry[]>([])
  const [isAutosaving, setIsAutosaving] = useState(false)
  const [isCopyingPreviousWeek, setIsCopyingPreviousWeek] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isLoading, setIsLoading] = useState(true) // Initial page load
  const [isLoadingWeek, setIsLoadingWeek] = useState(false) // Week navigation
  const [isSubmitting, setIsSubmitting] = useState(false) // Timesheet submission
  const [engagementSelectOpen, setEngagementSelectOpen] = useState(false)
  const [isLoadingEngagements, setIsLoadingEngagements] = useState(false)
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)

  /**
   * Calculates the total hours for the week from all weekly entries.
   * Sums up all hours and minutes across all engagements, tasks, and days.
   * @returns {number} Total hours as a decimal (e.g., 40.5 for 40h 30m)
   */
  const calculateWeekTotal = () => {
    const totalMinutes = weeklyEntries.reduce((sum, entry) => {
      return sum + Object.values(entry.times).reduce((tSum, time) => tSum + time.hours * 60 + time.minutes, 0)
    }, 0)
    return totalMinutes / 60
  }

  const weekTotalHours = calculateWeekTotal()
  
  /**
   * Formats total hours into a human-readable string (e.g., "40h 30m").
   * Handles conversion of 60+ minutes to additional hours.
   * @param {number} totalHours - Total hours as decimal
   * @returns {string} Formatted string like "40h 30m"
   */
  const formatWeeklyHours = (totalHours: number) => {
    const hours = Math.floor(totalHours)
    let minutes = Math.round((totalHours - hours) * 60)
    // Convert 60 minutes to 1 hour
    if (minutes >= 60) {
      const additionalHours = Math.floor(minutes / 60)
      const finalHours = hours + additionalHours
      minutes = minutes % 60
      return `${finalHours}h ${minutes}m`
    }
    return `${hours}h ${minutes}m`
  }

  /**
   * Aggregates duplicate entries (same engagement, task, and day) by:
   * - Adding hours and minutes together
   * - Keeping the comment from the entry with maximum total minutes
   * @param {any[]} lines - Array of timesheet line objects
   * @returns {any[]} Array with aggregated entries
   */
  const aggregateDuplicateEntries = (lines: any[]): any[] => {
    // Group by engagement-task-day combination
    const groupedByKey: { [key: string]: any[] } = {}
    
    lines.forEach((d) => {
      const dateObj = new Date(d.date)
      const weekday = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dateObj.getDay()]
      const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][dateObj.getMonth()]
      const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
      
      const engagementID = d.engagementID ?? d.EngagementID ?? 0
      const taskID = d.taskID ?? d.TaskID ?? 0
      const key = `${engagementID}-${taskID}-${label}`
      
      if (!groupedByKey[key]) {
        groupedByKey[key] = []
      }
      groupedByKey[key].push(d)
    })
    
    // Aggregate each group
    const aggregated: any[] = []
    Object.values(groupedByKey).forEach((group) => {
      if (group.length === 1) {
        // No duplicates, just add as-is
        aggregated.push(group[0])
      } else {
        // Multiple entries for same engagement-task-day
        // Find entry with maximum hours (this will be our primary entry)
        let maxEntry = group[0]
        let maxTotalMinutes = (maxEntry.hours ?? maxEntry.Hours ?? 0) * 60 + (maxEntry.minutes ?? maxEntry.Minutes ?? 0)
        
        let totalHours = 0
        let totalMinutes = 0
        const allLineIDs: number[] = [] // Track all lineIDs from duplicate entries
        
        group.forEach((entry) => {
          const hours = entry.hours ?? entry.Hours ?? 0
          const minutes = entry.minutes ?? entry.Minutes ?? 0
          const totalMins = hours * 60 + minutes
          
          totalHours += hours
          totalMinutes += minutes
          
          // Collect all lineIDs
          const lineID = entry.lineID ?? entry.LineID
          if (lineID) {
            allLineIDs.push(lineID)
          }
          
          if (totalMins > maxTotalMinutes) {
            maxTotalMinutes = totalMins
            maxEntry = entry
          }
        })
        
        // Convert excess minutes to hours
        const additionalHours = Math.floor(totalMinutes / 60)
        const finalHours = totalHours + additionalHours
        const finalMinutes = totalMinutes % 60
        
        // Get primary lineID (from maxEntry) and duplicate lineIDs (all others)
        const primaryLineID = maxEntry.lineID ?? maxEntry.LineID
        const duplicateLineIDs = allLineIDs.filter(id => id !== primaryLineID)
        
        // Create aggregated entry with sum of hours, primary lineID, and duplicate lineIDs
        aggregated.push({
          ...maxEntry,
          hours: finalHours,
          Hours: finalHours,
          minutes: finalMinutes,
          Minutes: finalMinutes,
          comment: maxEntry.comment ?? maxEntry.Comment ?? "",
          Comment: maxEntry.comment ?? maxEntry.Comment ?? "",
          lineID: primaryLineID,
          LineID: primaryLineID,
          duplicateLineIDs: duplicateLineIDs.length > 0 ? duplicateLineIDs : undefined,
        })
      }
    })
    
    return aggregated
  }

  /**
   * Calculates total hours for a specific day across all entries.
   * Used for day totals display and validation.
   * @param {string} dayFull - Full day name (e.g., "Monday, January 15")
   * @returns {number} Total hours for that day
   */
  const getDayHours = (dayFull: string) => {
    if (!dayFull) return 0
    const totalMinutes = weeklyEntries.reduce((sum, entry) => {
      const time = entry.times[dayFull] || { hours: 0, minutes: 0, comment: "" }
      return sum + time.hours * 60 + time.minutes
    }, 0)
    return totalMinutes / 60
  }

  /**
   * Validates if a date falls within an engagement's active date range.
   * Used to disable time entry for dates outside engagement period.
   * @param {number} engagementID - The engagement ID to check
   * @param {string} dayISO - Date in ISO format (YYYY-MM-DD)
   * @returns {boolean} True if date is within range or no range specified, false otherwise
   */
  const isDateWithinEngagementRange = (engagementID: number, dayISO: string): boolean => {
    const engagement = engagementOptions.find(e => e.engagementID === engagementID)
    if (!engagement || !engagement.startDate || !engagement.endDate) {
      // If no date range info, allow entry (backward compatibility)
      return true
    }
    
    try {
      const dayDate = new Date(dayISO)
      const startDate = new Date(engagement.startDate)
      const endDate = new Date(engagement.endDate)
      
      // Set time to midnight for accurate date comparison
      dayDate.setHours(0, 0, 0, 0)
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      
      return dayDate >= startDate && dayDate <= endDate
    } catch (e) {
      console.error("Error checking engagement date range", e)
      return true // Allow entry on error
    }
  }

  /**
   * Checks if a date is in the future using UTC date comparison.
   * Used to prevent time entry for future dates.
   * @param {string} dayISO - Date in ISO format (YYYY-MM-DD)
   * @returns {boolean} True if date is in the future, false otherwise
   */
  const isFutureDate = (dayISO: string): boolean => {
    if (!dayISO) return false
    
    try {
      // Get current UTC date (midnight)
      const now = new Date()
      const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      
      // Parse the dayISO date and set to UTC midnight
      const dayDate = new Date(dayISO + 'T00:00:00Z')
      const dayUTC = new Date(Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate()))
      
      // Compare dates - return true if day is after today
      return dayUTC > todayUTC
    } catch (e) {
      console.error("Error checking future date", e)
      return false // Allow entry on error
    }
  }

  // Keep a ref to latest weeklyEntries so debounced callbacks don't use stale closures.
  useEffect(() => {
    weeklyEntriesRef.current = weeklyEntries
  }, [weeklyEntries])

  /**
   * Autosaves time entry changes with 1-second debounce to reduce API calls.
   * Handles add, update, and delete operations based on lineID and time values.
   * Shows error toasts for validation failures but preserves user input.
   * @param {WeeklyTaskEntry} entry - The task entry being modified
   * @param {string} dayKey - Full day name (e.g., "Monday, January 15")
   * @param {boolean} immediate - If true, saves immediately without debounce (for onBlur)
   */
  const triggerAutosave = (entry: WeeklyTaskEntry, dayKey: string, immediate: boolean = false) => {
    const cellKey = `${entry.id}-${dayKey}`
    
    // Clear existing timeout for this cell to debounce rapid changes
    const existing = autosaveTimeoutsRef.current.get(cellKey)
    if (existing) {
      clearTimeout(existing)
      autosaveTimeoutsRef.current.delete(cellKey)
    }

    const performSave = async () => {
      // Prevent concurrent saves for the same cell (this is what creates duplicates)
      if (savingCellsRef.current.has(cellKey)) {
        pendingAutosavesRef.current.add(cellKey)
        return
      }

      // Debounced callbacks should read latest state, but immediate saves already
      // receive the freshest cell value from the event handler.
      const latestEntry = immediate ? entry : (weeklyEntriesRef.current.find(e => e.id === entry.id) ?? entry)
      const timeEntry = latestEntry.times[dayKey] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
      const day = weekDays.find(d => d.full === dayKey)
      if (!day || !day.iso) {
        console.warn("Day not found for autosave:", dayKey)
        return
      }

      // Don't make API call if lineID is null and both hours and minutes are 0 (prevent creating empty entries)
      if (!timeEntry.lineID && (timeEntry.hours || 0) === 0 && (timeEntry.minutes || 0) === 0) {
        return
      }

      // Calculate total day hours/minutes
      const allDayEntries = weeklyEntriesRef.current
        .filter(e => e.engagementID === latestEntry.engagementID)
        .map(e => e.times[dayKey] || { hours: 0, minutes: 0, comment: "", lineID: undefined })
      const dayTotalMinutes = allDayEntries.reduce((sum, t) => sum + t.hours * 60 + t.minutes, 0)
      const totalDayHours = Math.floor(dayTotalMinutes / 60)
      const totalDayMinutes = dayTotalMinutes % 60

      setIsAutosaving(true)
      savingCellsRef.current.add(cellKey)
      try {
        // Always use POST - backend handles add/update/delete based on lineID and hours/minutes
        // If no lineID -> add
        // If lineID exists and hours/minutes not both 0 -> update
        // If lineID exists and hours/minutes both 0 -> delete (backend handles this)
        const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            lineID: timeEntry.lineID ?? null, // Use existing lineID if available, otherwise null for new entry
            timesheetID: timesheetId ?? 0,
            engagementID: latestEntry.engagementID,
            taskID: latestEntry.taskID,
            hours: timeEntry.hours,
            minutes: timeEntry.minutes,
            date: `${day.iso}T00:00:00`,
            comment: timeEntry.comment || "",
            totalDayHours,
            totalDayMinutes,
            modUser: 0,
          })
        })

        // Get the response to extract status field (even if res.ok is false, API might return status -1)
        let responseData
        try {
          responseData = await res.json()
        } catch (e) {
          // If response is not JSON, throw the original error
          if (!res.ok) throw new Error(`${res.status}`)
          throw e
        }
        
        // Response format: {"status":2841,"message":"Timesheet Line updated!"} or {"status":1,"message":"..."} for delete
        // Error format: {"status":-1,"message":"Maximum daily hours limit exceeded. Please adjust the time."}
        const responseStatus = responseData?.status ?? responseData?.Status
        
        // If status is -1,-2, -3, or -4, it means the save failed - show error message but keep the cell value
        if (responseStatus === -1 ||responseStatus === -2|| responseStatus === -3 || responseStatus === -4) {
          const errorMessage = responseData?.message || "Failed to save time entry. Please try again."
          
          // Mark cell as failed
          setFailedCells(prev => new Set(prev).add(cellKey))
          
          // Show error toast for all errors
          toast({
            title: "Failed to save time entry",
            description: errorMessage,
            duration: 5000,
            className: "border border-red-300",
          })
          
          return
        }
        
        // If response is not OK and status is not -1, throw error
        if (!res.ok) throw new Error(`${res.status}`)
        
        // Save succeeded - clear failed state for this cell
        setFailedCells(prev => {
          const newSet = new Set(prev)
          newSet.delete(cellKey)
          return newSet
        })
        
        // If there are duplicate lineIDs, delete them after successful save
        // This prevents duplicate entries from persisting in the database
        const duplicateLineIDs = timeEntry.duplicateLineIDs || []
        if (duplicateLineIDs.length > 0 && responseStatus && responseStatus !== 1) {
          // Delete all duplicate lineIDs by sending delete requests (hours=0, minutes=0)
          // We do this in parallel but don't wait for all to complete - fire and forget
          duplicateLineIDs.forEach(async (duplicateLineID) => {
            try {
              await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/save`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  lineID: duplicateLineID,
                  timesheetID: timesheetId ?? 0,
                  engagementID: latestEntry.engagementID,
                  taskID: latestEntry.taskID,
                  hours: 0,
                  minutes: 0,
                  date: `${day.iso}T00:00:00`,
                  comment: "",
                  totalDayHours,
                  totalDayMinutes,
                  modUser: 0,
                })
              })
            } catch (e) {
              // Silently fail - duplicate deletion is best effort
              console.warn("Failed to delete duplicate lineID:", duplicateLineID, e)
            }
          })
        }
        
        // If status is 1, it means delete was successful - clear the lineID
        if (responseStatus === 1) {
          setWeeklyEntries(prev => prev.map(e => {
            if (e.id === latestEntry.id) {
              return {
                ...e,
                times: {
                  ...e.times,
                  [dayKey]: {
                    ...e.times[dayKey],
                    lineID: undefined,
                    duplicateLineIDs: undefined
                  }
                }
              }
            }
            return e
          }))
        }
        // If status is greater than 1 (lineID) and we don't have a lineID, update with the returned lineID (for new entries)
        // Note: status = 1 means delete, so we exclude it here
        else if (responseStatus && responseStatus > 1 && !timeEntry.lineID) {
          setWeeklyEntries(prev => prev.map(e => {
            if (e.id === latestEntry.id) {
              return {
                ...e,
                times: {
                  ...e.times,
                  [dayKey]: {
                    ...e.times[dayKey],
                    lineID: responseStatus
                  }
                }
              }
            }
            return e
          }))
        }
        // If we successfully updated and had duplicates, clear the duplicateLineIDs
        else if (responseStatus && responseStatus > 1 && duplicateLineIDs.length > 0) {
          setWeeklyEntries(prev => prev.map(e => {
            if (e.id === latestEntry.id) {
              return {
                ...e,
                times: {
                  ...e.times,
                  [dayKey]: {
                    ...e.times[dayKey],
                    duplicateLineIDs: undefined
                  }
                }
              }
            }
            return e
          }))
        }
      } catch (e) {
        console.error("Failed to autosave entry", e)
        // Mark cell as failed
        setFailedCells(prev => new Set(prev).add(cellKey))
        // Show user-friendly error message
        toast({
          title: "Failed to save",
          description: "Unable to save time entry. Please check your connection and try again.",
          duration: 5000,
          className: "border border-red-300",
        })
      } finally {
        savingCellsRef.current.delete(cellKey)
        setIsAutosaving(false)
        if (pendingAutosavesRef.current.has(cellKey)) {
          pendingAutosavesRef.current.delete(cellKey)
          setTimeout(() => {
            void performSave()
          }, 0)
        }
      }
    }

    if (immediate) {
      performSave()
    } else {
      // Set new timeout for 1 second debounce
      const t = setTimeout(() => {
        autosaveTimeoutsRef.current.delete(cellKey)
        void performSave()
      }, 1000)
      autosaveTimeoutsRef.current.set(cellKey, t)
    }
  }

  /**
   * Calculates total minutes for the week (used for submission).
   * @returns {number} Total minutes across all entries
   */
  const getWeekTotalsMinutes = () => {
    return weeklyEntries.reduce((sum, entry) => {
      return sum + Object.values(entry.times).reduce((tSum, time) => tSum + time.hours * 60 + time.minutes, 0)
    }, 0)
  }

  const weekdaysFilled = weekDays
    .filter((day) => !day.isWeekend)
    .every((day) => {
      return getDayHours(day.full) >= 8
    })

  /**
   * Calculates week total hours excluding weekends (for validation).
   * Used to ensure minimum 40 hours requirement is met.
   * @returns {number} Total hours for weekdays only
   */
  const calculateWeekTotalExcludingWeekends = () => {
    const weekdays = weekDays.filter((d) => !d.isWeekend)
    if (weekdays.length === 0) return 0
    const totalMinutes = weekdays.reduce((sum, day) => {
      return sum + weeklyEntries.reduce((tSum, entry) => {
        const time = entry.times[day.full] || { hours: 0, minutes: 0, comment: "" }
        return tSum + time.hours * 60 + time.minutes
      }, 0)
    }, 0)
    return totalMinutes / 60
  }

  const weekTotalHoursExcludingWeekends = calculateWeekTotalExcludingWeekends()

  /** True if every task row has at least one day with hours or minutes > 0 (for submit validation). */
  const everyLineHasTime =
    weeklyEntries.length === 0 ||
    !weeklyEntries.some((entry) => {
      const hasAny = weekDays.some((day) => {
        const t = entry.times[day.full] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
        return (t.hours ?? 0) > 0 || (t.minutes ?? 0) > 0
      })
      return !hasAny
    })

  const today = new Date()

  /** Build week-day objects (Mon–Sun) from a Monday date. Used so header stays in sync when navigating (e.g. Go To Current TS). */
  const buildWeekDaysFromMonday = (monday: Date) => {
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    const fullDayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    const toISODate = (d: Date) => `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`
    const out: { day: string; date: string; full: string; status: string; isWeekend: boolean; iso: string }[] = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday)
      d.setDate(monday.getDate() + i)
      d.setHours(0, 0, 0, 0)
      const isWeekend = i >= 5
      const isPastOrToday = d <= today
      out.push({
        day: dayNames[i],
        date: d.getDate().toString().padStart(2, "0"),
        full: `${fullDayNames[i]}, ${monthNames[d.getMonth()]} ${d.getDate()}`,
        status: isPastOrToday ? (isWeekend ? "optional" : "filled") : "future",
        isWeekend,
        iso: toISODate(d),
      })
    }
    return out
  }

  /**
   * Updates the weekDays state based on a week string.
   * Parses the week string and generates day objects for Mon-Sun.
   * @param {string} weekString - Week string format: "Jul 28 - Aug 03, 2025" (Monday to Friday)
   * @param {boolean} preserveSelectedDate - If true, keeps current selectedDate if it exists in new week
   */
  const updateWeekDays = (weekString: string, preserveSelectedDate: boolean = false) => {
    if (!weekString) {
      console.warn("updateWeekDays called with empty weekString")
      return
    }
    
    // Parse week string like "Jul 28 - Aug 03, 2025" (start is Monday)
    const parts = weekString.split(", ")
    if (parts.length !== 2) {
      console.error("Invalid week string format:", weekString)
      return
    }
    
    const [range, yearStr] = parts
    const rangeParts = range.split(" - ")
    if (rangeParts.length === 0) {
      console.error("Invalid week range format:", range)
      return
    }
    
    const [startStr] = rangeParts
    const startParts = startStr.split(" ")
    if (startParts.length !== 2) {
      console.error("Invalid start date format:", startStr)
      return
    }
    
    const [month, day] = startParts

    const monthMap: { [key: string]: number } = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    }

    if (!(month in monthMap)) {
      console.error("Invalid month abbreviation:", month)
      return
    }

    const year = Number.parseInt(yearStr)
    const dayNum = Number.parseInt(day)
    
    if (isNaN(year) || isNaN(dayNum)) {
      console.error("Invalid year or day:", yearStr, day)
      return
    }
    
    const monday = new Date(year, monthMap[month], dayNum)
    
    // Validate date
    if (isNaN(monday.getTime())) {
      console.error("Invalid date created:", year, monthMap[month], dayNum)
      return
    }

    const newWeekDays = [] as any[]
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    const fullDayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ]

    const toISODate = (date: Date) => {
      const y = date.getFullYear()
      const m = (date.getMonth() + 1).toString().padStart(2, "0")
      const d = date.getDate().toString().padStart(2, "0")
      return `${y}-${m}-${d}`
    }

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(monday)
      currentDate.setDate(monday.getDate() + i)

      const isWeekend = i >= 5
      const isPastOrToday = currentDate <= today

      newWeekDays.push({
        day: dayNames[i],
        date: currentDate.getDate().toString().padStart(2, "0"),
        full: `${fullDayNames[i]}, ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}`,
        status: isPastOrToday ? (isWeekend ? "optional" : "filled") : "future",
        isWeekend,
        iso: toISODate(currentDate),
      })
    }

    setWeekDays(newWeekDays)
    
    // Only change selected date if not preserving, or if current date not in new week
    if (!preserveSelectedDate) {
      setSelectedDate(newWeekDays[0].full)
    } else {
      // Check if current selectedDate exists in new week
      const dateStillValid = newWeekDays.some(d => d.full === selectedDate)
      if (!dateStillValid) {
        setSelectedDate(newWeekDays[0].full)
      }
      // else keep the current selectedDate
    }
  }

  useEffect(() => {
    if (!currentWeek) return
    updateWeekDays(currentWeek)
  }, [currentWeek])

  /**
   * Converts a Date object to ISO date string (YYYY-MM-DD).
   * @param {Date} date - Date object to convert
   * @returns {string} ISO date string
   */
  const toISODate = (date: Date) => {
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date passed to toISODate:", date)
      return ""
    }
    const y = date.getFullYear()
    const m = (date.getMonth() + 1).toString().padStart(2, "0")
    const d = date.getDate().toString().padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  /**
   * Formats date as ISO datetime string for API calls.
   * @param {Date} date - Date object to convert
   * @param {boolean} isEndOfDay - If true, sets time to 23:59:59, otherwise 00:00:00
   * @returns {string} ISO datetime string (e.g., "2025-11-17T00:00:00")
   */
  const toISODateTime = (date: Date, isEndOfDay: boolean = false) => {
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date passed to toISODateTime:", date)
      return ""
    }
    const y = date.getFullYear()
    const m = (date.getMonth() + 1).toString().padStart(2, "0")
    const d = date.getDate().toString().padStart(2, "0")
    if (isEndOfDay) {
      return `${y}-${m}-${d}T23:59:59`
    }
    return `${y}-${m}-${d}T00:00:00`
  }

  /**
   * Calculates week start (Monday 00:00:00) and end (Sunday 23:59:59) dates.
   * Used for API calls that require week date ranges.
   * @param {Date | string} mondayDate - Monday date of the week
   * @returns {{startDate: string, endDate: string}} ISO datetime strings for week boundaries
   */
  const getWeekStartAndEndDates = (mondayDate: Date | string) => {
    const monday = typeof mondayDate === 'string' ? new Date(mondayDate) : new Date(mondayDate)
    if (isNaN(monday.getTime())) {
      console.error("Invalid mondayDate passed to getWeekStartAndEndDates:", mondayDate)
      return { startDate: "", endDate: "" }
    }
    monday.setHours(0, 0, 0, 0)
    
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    sunday.setHours(23, 59, 59, 999)
    
    return {
      startDate: toISODateTime(monday, false),
      endDate: toISODateTime(sunday, true)
    }
  }

  /**
   * Checks if a given Monday date matches the current timesheet week from v1 API.
   * Used to determine if "Go To Current TS" button should be disabled.
   * @param {Date} monday - Monday date to check
   * @returns {boolean} True if week matches current week
   */
  const checkIfWeekMatchesCurrentWeek = (monday: Date) => {
    if (!v1CurrentWeekStartDate) return false
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    return monday.getTime() === v1CurrentWeekStartDate.getTime() && friday.getTime() === v1CurrentWeekEndDate?.getTime()
  }

  /**
   * Loads timesheet entries for a specific week using GetTSbyWeek API.
   * Updates weeklyEntries, engagement options, and timesheet status.
   * Handles both current week and historical week views.
   * @param {Date} monday - Monday date of the week to load
   */
  const loadWeekEntries = async (monday: Date) => {
    if (!monday || isNaN(monday.getTime())) {
      console.error("Invalid monday date passed to loadWeekEntries:", monday)
      setWarnMessage("Invalid date. Please try again.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    
    setIsLoadingWeek(true)
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)

    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/GetTSbyWeek`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userID: 0,
          startDate: toISODate(monday),
          endDate: toISODate(friday),
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const response: { timesheetID?: number; status?: number; lines?: any[] } = await res.json()
      const data: any[] = response.lines ?? []

      // Extract timesheetID and status from root of response
      const weekTimesheetId = response.timesheetID
      if (weekTimesheetId) {
        setSelectedWeekTimesheetId(weekTimesheetId)
        setTimesheetId(weekTimesheetId)
      } else {
        setTimesheetId(null)
        setSelectedWeekTimesheetId(null)
      }
      
      const statusVal = response.status
      setTimesheetStatus(typeof statusVal === "number" ? statusVal : null)

      // Aggregate duplicate entries (same engagement, task, and day)
      const aggregatedData = aggregateDuplicateEntries(data)

      // Map API data to weeklyEntries structure
      const grouped: { [key: string]: WeeklyTaskEntry } = {}
      
      aggregatedData.forEach((d) => {
        const dateObj = new Date(d.date)
        const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()]
        const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][dateObj.getMonth()]
        const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
        
        const engagementID = d.engagementID ?? d.EngagementID ?? 0
        const taskID = d.taskID ?? d.TaskID ?? 0
        const key = `${engagementID}-${taskID}`
        
        if (!grouped[key]) {
          grouped[key] = {
            id: key,
            engagementID: engagementID,
            engagementName: d.engagementName ?? d.EngagementName ?? "",
            taskID: taskID,
            taskName: d.taskName ?? d.TaskName ?? "",
            times: {}
          }
        }
        
        grouped[key].times[label] = {
          hours: d.hours ?? d.Hours ?? 0,
          minutes: d.minutes ?? d.Minutes ?? 0,
          comment: decodeHtmlEntities(d.comment ?? d.Comment ?? ""),
          lineID: d.lineID ?? d.LineID ?? undefined,
          duplicateLineIDs: (d as any).duplicateLineIDs ?? undefined,
        }
      })
      
      setWeeklyEntries(Object.values(grouped))
      
      // Check if this week matches current week and disable button accordingly
      const matchesCurrentWeek = checkIfWeekMatchesCurrentWeek(monday)
      setIsGoToCurrentTSDisabled(matchesCurrentWeek)
      
      // Extract unique engagements from the response to populate dropdown
      const uniqueEngagements = new Map<number, { engagementID: number; title: string }>()
      data.forEach((d) => {
        const engagementID = d.engagementID ?? d.EngagementID ?? 0
        const engagementName = d.engagementName ?? d.EngagementName ?? ""
        if (engagementID > 0 && engagementName && !uniqueEngagements.has(engagementID)) {
          uniqueEngagements.set(engagementID, { engagementID, title: engagementName })
        }
      })
      
      // Get engagement IDs from timesheet data first
      const engIds = new Set(Object.values(grouped).map(e => e.engagementID))
      
      // API call removed - engagements will be loaded only when dropdown is opened
      // See onOpenChange handler in the Select component for engagement loading
      // For now, use engagements from timesheet data only
      const timesheetEngsArray = Array.from(uniqueEngagements.values())
      if (timesheetEngsArray.length > 0) {
        setEngagementOptions(timesheetEngsArray)
      }
      
      // Set selected engagements - React will batch this with the above
      setSelectedEngagements(engIds)
      
      // Auto-expand all engagements that have entries
      setExpandedEngagements(engIds)
      
      setIsViewingSpecificWeek(true)
    } catch (e) {
      console.error("Failed to load week entries", e)
      setWeeklyEntries([])
      setIsViewingSpecificWeek(true)
      // Show user-friendly error message
      setWarnMessage("Failed to load week data. Please try again.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
    } finally {
      setIsLoadingWeek(false)
    }
  }

  /**
   * Reloads current timesheet data based on viewing mode.
   * If viewing specific week, reloads that week; otherwise reloads current timesheet.
   * Used after save/update operations to refresh data.
   */
  const reloadCurrentData = async () => {
    if (isViewingSpecificWeek) {
      if (!currentWeek) {
        console.warn("Cannot reload: currentWeek is empty")
        await loadCurrentTimesheet()
        return
      }
      try {
        const [range, yearStr] = currentWeek.split(", ")
        if (!range || !yearStr) {
          throw new Error("Invalid currentWeek format")
        }
        const [startStr] = range.split(" - ")
        if (!startStr) {
          throw new Error("Invalid week range format")
        }
        const [monAbbr, dayStr] = startStr.split(" ")
        if (!monAbbr || !dayStr) {
          throw new Error("Invalid start date format")
        }
        const mm: { [k: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
        if (mm[monAbbr] === undefined) {
          throw new Error(`Invalid month abbreviation: ${monAbbr}`)
        }
        const monday = new Date(Number.parseInt(yearStr), mm[monAbbr], Number.parseInt(dayStr))
        if (isNaN(monday.getTime())) {
          throw new Error("Invalid date created")
        }
        await loadWeekEntries(monday)
      } catch (e) {
        console.error("Failed to reload specific week", e)
        // Fallback to loading current timesheet
        await loadCurrentTimesheet()
      }
    } else {
      await loadCurrentTimesheet()
    }
  }

  /**
   * Loads the current timesheet data from v1 API.
   * This is the main data loading function called on page mount.
   * Sets up week dates, engagements, entries, and navigation restrictions.
   */
  const loadCurrentTimesheet = async () => {
    setIsLoading(true)
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/v1`, { credentials: "include" })
      if (!res.ok) {
        throw new Error(`API returned status ${res.status}`)
      }
      const payload = await res.json()
      const data = payload?.data
      const lines: any[] = data?.timesheetLines || []

      // Store current week dates from v1 for comparison
      let currentWeekStart: Date | null = null
      let currentWeekEnd: Date | null = null
      if (data?.currentWeekStartDate && data?.currentWeekEndDate) {
        currentWeekStart = new Date(data.currentWeekStartDate)
        currentWeekEnd = new Date(data.currentWeekEndDate)
        const toISO = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
        setV1CurrentStartISO(toISO(currentWeekStart))
        setV1CurrentEndISO(toISO(currentWeekEnd))
        setV1CurrentWeekStartDate(currentWeekStart)
        setV1CurrentWeekEndDate(currentWeekEnd)
      }
      
      // Store first timesheet dates to restrict navigation
      if (data?.firstTimesheetStartDate && data?.firstTimesheetEndDate) {
        const firstStart = new Date(data.firstTimesheetStartDate)
        const toISO = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
        setFirstTimesheetStartDate(firstStart)
        setFirstTimesheetStartISO(toISO(firstStart))
      }
      setRejectedCount(data?.totalRejectedCount ?? 0)
      setRejectedRanges(Array.isArray(data?.rejectedTimesheetRanges) ? data.rejectedTimesheetRanges : [])

      // Determine which week to display: use timesheet start/end if present, else current week from v1
      const start = (data?.startDate && data?.endDate)
        ? new Date(data.startDate)
        : currentWeekStart
      const end = (data?.startDate && data?.endDate)
        ? new Date(data.endDate)
        : currentWeekEnd

      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
        const wk = `${monthsAbbr[start.getMonth()]} ${start.getDate().toString().padStart(2,'0')} - ${monthsAbbr[end.getMonth()]} ${end.getDate().toString().padStart(2,'0')}, ${start.getFullYear()}`
        setCurrentWeek(wk)
        updateWeekDays(wk, true)  // Pass true to preserve selected date

        // Check if displayed week matches current week from v1
        if (currentWeekStart && currentWeekEnd) {
          const datesMatch = start.getTime() === currentWeekStart.getTime() && end.getTime() === currentWeekEnd.getTime()
          setIsGoToCurrentTSDisabled(datesMatch)
        } else {
          setIsGoToCurrentTSDisabled(false)
        }

        // Load engagements for the week using GetEngagementsByUserAndWeek
        const { startDate: weekStartDate, endDate: weekEndDate } = getWeekStartAndEndDates(start)
        const startDateEncoded = encodeURIComponent(weekStartDate)
        const endDateEncoded = encodeURIComponent(weekEndDate)

        fetch(process.env.REACT_APP_API_URL + `/api/Engagement/GetEngagementsByUserAndWeek/${startDateEncoded}/${endDateEncoded}`, { credentials: "include" })
          .then(r => r.ok ? r.json() : Promise.reject(r.status))
          .then((engData: { validation: { status: number; message: string }; engagements: any[] }) => {
            if (engData.engagements && Array.isArray(engData.engagements)) {
              const apiEngagements = engData.engagements.map(x => ({
                engagementID: x.engagementID,
                title: x.title ?? String(x.engagementID),
                startDate: x.startDate,
                endDate: x.endDate
              }))

              // Extract engagements from timesheet lines (available now)
              const timesheetEngagements = new Map<number, { engagementID: number; title: string; startDate?: string; endDate?: string }>()
              lines.forEach((d) => {
                const engagementID = d.engagementID ?? d.EngagementID ?? 0
                const engagementName = d.engagementName ?? d.EngagementName ?? ""
                if (engagementID > 0 && engagementName && !timesheetEngagements.has(engagementID)) {
                  timesheetEngagements.set(engagementID, {
                    engagementID: engagementID,
                    title: engagementName
                  })
                }
              })

              // Merge API engagements with timesheet engagements
              setEngagementOptions(prev => {
                const apiEngIds = new Set(apiEngagements.map(e => e.engagementID))
                const timesheetEngsArray = Array.from(timesheetEngagements.values())
                const toAddFromTimesheet = timesheetEngsArray.filter(e => !apiEngIds.has(e.engagementID))
                const existingNotInApi = prev.filter(e => !apiEngIds.has(e.engagementID))
                return [...apiEngagements, ...existingNotInApi, ...toAddFromTimesheet]
              })
            }
          })
          .catch(() => {
            console.error("Failed to load engagements from GetEngagementsByUserAndWeek")
            toast({
              title: "Could not load full engagement list",
              description: "You can still add from existing entries. Try opening the engagement dropdown again later.",
              duration: 4000,
              className: "border border-amber-200",
            })
          })
      }
      
      if (data?.timesheetID) setTimesheetId(data.timesheetID)
      if (typeof data?.status === 'number') setTimesheetStatus(data.status)
      setIsViewingSpecificWeek(false)
      
      // Aggregate duplicate entries (same engagement, task, and day)
      const aggregatedLines = aggregateDuplicateEntries(lines)
      
      // Map API data to weeklyEntries structure
      const grouped: { [key: string]: WeeklyTaskEntry } = {}
      
      aggregatedLines.forEach((d) => {
        const dateObj = new Date(d.date)
        const weekday = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dateObj.getDay()]
        const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][dateObj.getMonth()]
        const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
        
        const engagementID = d.engagementID ?? d.EngagementID ?? 0
        const taskID = d.taskID ?? d.TaskID ?? 0
        const key = `${engagementID}-${taskID}`
        
        if (!grouped[key]) {
          grouped[key] = {
            id: key,
            engagementID: engagementID,
            engagementName: d.engagementName ?? d.EngagementName ?? "",
            taskID: taskID,
            taskName: d.taskName ?? d.TaskName ?? "",
            times: {}
          }
        }
        
        grouped[key].times[label] = {
          hours: d.hours ?? d.Hours ?? 0,
          minutes: d.minutes ?? d.Minutes ?? 0,
          comment: decodeHtmlEntities(d.comment ?? d.Comment ?? ""),
          lineID: d.lineID ?? d.LineID ?? undefined,
          duplicateLineIDs: (d as any).duplicateLineIDs ?? undefined,
        }
      })
      
      setWeeklyEntries(Object.values(grouped))
      
      // Extract unique engagements from the response to populate dropdown
      const uniqueEngagements = new Map<number, { engagementID: number; title: string }>()
      lines.forEach((d) => {
        const engagementID = d.engagementID ?? d.EngagementID ?? 0
        const engagementName = d.engagementName ?? d.EngagementName ?? ""
        if (engagementID > 0 && engagementName && !uniqueEngagements.has(engagementID)) {
          uniqueEngagements.set(engagementID, { engagementID, title: engagementName })
        }
      })
      // Update engagement options with unique engagements from API (merge with existing)
      if (uniqueEngagements.size > 0) {
        setEngagementOptions(prev => {
          const existingIds = new Set(prev.map(e => e.engagementID))
          const toAdd = Array.from(uniqueEngagements.values()).filter(e => !existingIds.has(e.engagementID))
          return [...prev, ...toAdd]
        })
      }
      
      // Set selected engagements
      const engIds = new Set(Object.values(grouped).map(e => e.engagementID))
      setSelectedEngagements(engIds)
      
      // Auto-expand all engagements that have entries
      setExpandedEngagements(engIds)
    } catch (e) {
      console.error("Failed to load current timesheet", e)
      setWeeklyEntries([])
      setIsViewingSpecificWeek(false)
      // Show user-friendly error message
      setWarnMessage("Failed to load timesheet. Please refresh the page.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 5000)
    } finally {
      setIsLoading(false)
    }
  }




  /**
   * Validates timesheet before submission and opens confirmation dialog.
   * Checks:
   * - All weekdays have at least 8 hours
   * - Total week hours (excluding weekends) is at least 40
   * - Every line (task row) has at least one day with hours or minutes > 0
   */
  const handleSubmitTimesheet = () => {
    if (!timesheetId) {
      setWarnMessage("Please create a timesheet first.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    
    // Check if all weekdays have at least 8 hours using weeklyEntries
    const weekdays = weekDays.filter((d) => !d.isWeekend)
    if (weekdays.length === 0) {
      setWarnMessage("No weekdays found. Please refresh the page.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    
    const anyWeekdayUnderMin = weekdays.some((d) => {
      const dayHours = getDayHours(d.full)
      return dayHours < 8
    })
    
    if (anyWeekdayUnderMin) {
      setWarnMessage("Please fill min daily hours (8 hours per day).")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    
    // Also check if total hours (excluding weekends) is at least 40
    if (weekTotalHoursExcludingWeekends < 40) {
      setWarnMessage("Please fill minimum 40 hours for the week (excluding weekends).")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      return
    }
    
    // Check if there is any task row that doesn't have at least one day with both lineID and valid hours
    // This covers two cases:
    // 1. Task row that was never saved (no lineID)
    // 2. Task row that was copied from previous week but has 0 hours (has lineID but no valid time)
    const entryWithNoValidTime = weeklyEntries.find((entry) => {
      // Check if this entry has at least one day with both lineID and valid hours/minutes
      const hasValidTimeEntry = weekDays.some((day) => {
        const t = entry.times[day.full] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
        // Must have lineID AND valid time (hours > 0 or minutes > 0)
        return (t.lineID !== undefined && t.lineID !== null) && ((t.hours ?? 0) > 0 || (t.minutes ?? 0) > 0)
      })
      return !hasValidTimeEntry
    })
    if (entryWithNoValidTime) {
      setWarnMessage(
        `"${entryWithNoValidTime.taskName}" under "${entryWithNoValidTime.engagementName}" has no hours entered. Please enter time or remove the empty line.`
      )
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 5000)
      return
    }
    
    // Check if there are any cells with failed saves (red borders)
    if (failedCells.size > 0) {
      setWarnMessage("Please correct or remove the cells with errors (red borders) before submitting the timesheet.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 5000)
      return
    }
    
    setIsSubmitOpen(true)
  }

  /**
   * Submits the timesheet for approval.
   * Calculates totals, sends to API, then reloads data to show updated status.
   * Handles success and error cases with appropriate user feedback.
   */
  const doSubmitTimesheet = async () => {
    if (!timesheetId) {
      setWarnMessage("No timesheet ID found. Please refresh the page.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 3000)
      setIsSubmitOpen(false)
      return
    }
    
    setIsSubmitting(true)
    try {
      const totalWeekMin = getWeekTotalsMinutes()
      const hoursTotal = Math.floor(totalWeekMin / 60)
      const minutesTotal = totalWeekMin % 60
      
      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/Submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          timesheetID: timesheetId ?? 0,
          userID: 0,
          hoursTotal,
          minutesTotal,
          submissionComment: submitComment,
        })
      })
      if (!res.ok) throw new Error(`${res.status}`)
      
      // Clear all red borders after successful submit
      setFailedCells(new Set())
      
      setIsSubmitOpen(false)
      setSubmitComment("")
      setSuccessMessage("Timesheet Submitted Successfully")
      setShowSuccessToast(true)
      setTimeout(() => setShowSuccessToast(false), 3000)

      await new Promise(r => setTimeout(r, 800))
      try {
        const v1Res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/v1`, { credentials: "include" })
        if (v1Res.ok) {
          const p = await v1Res.json()
          const d = p?.data
          if (d?.currentWeekStartDate && d?.currentWeekEndDate) {
            const monday = new Date(d.currentWeekStartDate)
            const friday = new Date(d.currentWeekEndDate)
            await loadWeekEntries(monday)
            const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
            const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
            setCurrentWeek(wk)
            updateWeekDays(wk)
            setIsViewingSpecificWeek(true)
          } else {
            setIsViewingSpecificWeek(false)
            await loadCurrentTimesheet()
          }
        } else {
          setIsViewingSpecificWeek(false)
          await loadCurrentTimesheet()
        }
      } catch {
        setIsViewingSpecificWeek(false)
        await loadCurrentTimesheet()
      }
      await loadCurrentTimesheet()
    } catch (e) {
      console.error("Failed to submit timesheet", e)
      setWarnMessage("Failed to submit timesheet. Please try again.")
      setShowWarnToast(true)
      setTimeout(() => setShowWarnToast(false), 5000)
      // Keep dialog open on error so user can retry
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handles bulk entry submission from BulkEntryModal.
   * Converts bulk entries to weeklyEntries format and updates state.
   * Merges with existing entries if engagement/task combination already exists.
   * @param {any[]} bulkEntries - Array of bulk entry objects with engagement, task, hours, minutes, comments
   * @param {string[]} selectedDays - Array of day keys (e.g., ["Mon", "Tue"])
   */
  const handleBulkSubmit = (bulkEntries: any[], selectedDays: string[]) => {
    if (!bulkEntries || bulkEntries.length === 0) {
      toast({
        title: "No entries to add",
        description: "Please add at least one engagement and task with time.",
        duration: 3000,
        className: "border border-amber-200",
      })
      return
    }
    if (!selectedDays || selectedDays.length === 0) {
      toast({
        title: "No days selected",
        description: "Please select at least one day for the bulk entry.",
        duration: 3000,
        className: "border border-amber-200",
      })
      return
    }
    
    // Convert bulk entries to weeklyEntries format
    selectedDays.forEach((dayKey) => {
      const dayData = weekDays.find((d) => d.day === dayKey)
      if (!dayData || !dayData.full) {
        console.warn(`Day not found for key: ${dayKey}`)
        return
      }

      bulkEntries.forEach((bulkEntry) => {
        if (!bulkEntry || !bulkEntry.engagement || !bulkEntry.task) {
          console.warn("Invalid bulk entry:", bulkEntry)
          return
        }
        
        const engagement = engagementOptions.find(e => e.title === bulkEntry.engagement)
        const task = taskOptions.find(t => t.taskName === bulkEntry.task)
        
        if (!engagement || !task) {
          console.warn(`Engagement or task not found: ${bulkEntry.engagement}, ${bulkEntry.task}`)
          return
        }

        const key = `${engagement.engagementID}-${task.taskID}`
        const existingEntry = weeklyEntries.find(e => e.id === key)

        if (existingEntry) {
          // Update existing entry
          setWeeklyEntries(prev => prev.map(e => 
            e.id === key
              ? {
                  ...e,
                  times: {
                    ...e.times,
                    [dayData.full]: {
                      hours: bulkEntry.hours,
                      minutes: bulkEntry.minutes,
                      comment: bulkEntry.comments || "",
                      lineID: e.times[dayData.full]?.lineID
                    }
                  }
                }
              : e
          ))
        } else {
          // Create new entry
          const newEntry: WeeklyTaskEntry = {
            id: key,
            engagementID: engagement.engagementID,
            engagementName: engagement.title,
            taskID: task.taskID,
            taskName: task.taskName,
            times: {
              [dayData.full]: {
                hours: bulkEntry.hours,
                minutes: bulkEntry.minutes,
                comment: bulkEntry.comments || "",
                lineID: undefined
              }
            }
          }
          setWeeklyEntries(prev => [...prev, newEntry])
          setSelectedEngagements(prev => new Set([...prev, engagement.engagementID]))
        }
      })
    })
  }

  // Initial data load on component mount
  useEffect(() => {
    loadCurrentTimesheet()
    
    // Cleanup autosave timeout on unmount to prevent memory leaks
    return () => {
      autosaveTimeoutsRef.current.forEach((t) => clearTimeout(t))
      autosaveTimeoutsRef.current.clear()
    }
  }, [])

  // Transformation logic removed - we now load directly into weeklyEntries from APIs

  // Engagements are loaded when dropdown opens (see onOpenChange handler in Select component)


  return (
    <TooltipProvider delayDuration={300}>
      <div className={`p-3 sm:p-6 space-y-4 sm:space-y-6 text-gray-900 bg-gray-50`}>
        {/* Nags Section */}
        <div className="space-y-2">
          {showNag1 && rejectedCount > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-red-100 border border-red-300 rounded-lg p-3 gap-2">
              <div className="text-sm text-red-800 pr-2">
                {rejectedCount > 0 ? (
                  <span>
                    You have {rejectedCount} rejected timesheets:
                    {rejectedRanges.map((r, idx) => {
                      const s = new Date(r.startDate)
                      const e = new Date(r.endDate)
                      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                      const label = `${months[s.getMonth()]} '${String(s.getFullYear()).slice(-2)} (${s.getDate()}-${e.getDate()})`
                      return (
                        <button
                          key={r.timesheetID}
                          className="underline text-blue-700 hover:text-blue-900 ml-1"
                            onClick={async () => {
                              if (isLoadingWeek) return // Prevent multiple clicks
                              try {
                                if (!r.startDate || !r.endDate) {
                                  throw new Error("Invalid date range in rejected timesheet")
                                }
                                const monday = new Date(r.startDate)
                                const friday = new Date(r.endDate)
                                
                                if (isNaN(monday.getTime()) || isNaN(friday.getTime())) {
                                  throw new Error("Invalid date format")
                                }
                                
                                // Set week and update days FIRST, then load entries
                                // This ensures the week context is set before loading data
                                const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                                const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                                setCurrentWeek(wk)
                                updateWeekDays(wk)
                                
                                // Then call GetTSbyWeek API to load the data
                                await loadWeekEntries(monday)
                                
                                // Ensure we're viewing specific week
                                setIsViewingSpecificWeek(true)
                              } catch (e) {
                                console.error("Failed to load rejected timesheet", e)
                                setWarnMessage("Failed to load rejected timesheet. Please try again.")
                                setShowWarnToast(true)
                                setTimeout(() => setShowWarnToast(false), 3000)
                              }
                            }}
                        >
                          {label}{idx < rejectedRanges.length - 1 ? ',' : ''}
                        </button>
                      )
                    })}
                  </span>
                ) : (
                  <span>No rejected timesheets.</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <X
                  className="h-4 w-4 cursor-pointer text-gray-500"
                  onClick={() => setShowNag1(false)}
                />
              </div>
            </div>
          )}

          {showPopup && (
            <div
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                backgroundColor: "#708090",
                color: "white",
                padding: "10px 20px",
                borderRadius: "8px",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                zIndex: 1000,
                height: "50px",
                width: "240px",
                textAlign: "center",
              }}
            >
              Notified Successfully
            </div>
          )}
        </div>

        {/* Combined Week Navigation and Timesheet Table */}
        <Card className={`bg-white border-gray-200`}>
          <CardHeader className="px-3 sm:px-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <h1 className={`text-lg sm:text-xl font-semibold text-gray-800`}>Add/View Timesheet</h1>
              </div>
              <Dialog open={isGuidelinesOpen} onOpenChange={setIsGuidelinesOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 p-0 h-auto text-blue-600 hover:text-blue-700"
                  >
                    <Info className="w-4 h-4" />
                    <span className="font-medium text-sm sm:text-base">Timesheet Guidelines</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-gray-900">Timesheet Guidelines</DialogTitle>
                  </DialogHeader>
                  <div className={`p-3 sm:p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-800`}>
                    <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                      <li>• Working days: Monday to Friday (5 days per week)</li>
                      <li>• Minimum working hours: 8 hours per day, 40 hours per week</li>
                      <li>• Saturday and Sunday are optional working days</li>
                      <li>• Weekend entries are only available after completing all weekday timesheets</li>
                      <li>• Fill previous week timesheets before accessing future weeks</li>
                      <li>• All entries must include engagement, task, and time details</li>
                    </ul>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* All controls in one line */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                className="font-medium bg-white"
                disabled={(() => {
                  // Disable if loading or navigating would go before first timesheet start date
                  if (isLoadingWeek) return true
                  if (!currentWeek || !firstTimesheetStartDate) return false
                  try {
                    const [range, yearStr] = currentWeek.split(", ")
                    const [startStr] = range.split(" - ")
                    const monthsAbbr: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
                    const [monAbbr, dayStr] = startStr.split(" ")
                    const currentMonday = new Date(Number.parseInt(yearStr), monthsAbbr[monAbbr], Number.parseInt(dayStr))
                    const previousMonday = new Date(currentMonday)
                    previousMonday.setDate(currentMonday.getDate() - 7)
                    // Disable if previous week would be before first timesheet start date
                    return previousMonday < firstTimesheetStartDate
                  } catch {
                    return false
                  }
                })()}
                onClick={async () => {
                  // Navigate to previous week
                  if (!currentWeek) {
                    setWarnMessage("No week selected. Please select a week first.")
                    setShowWarnToast(true)
                    setTimeout(() => setShowWarnToast(false), 3000)
                    return
                  }
                  if (isLoadingWeek) return // Prevent multiple clicks
                  
                  try {
                    const [range, yearStr] = currentWeek.split(", ")
                    if (!range || !yearStr) {
                      throw new Error("Invalid currentWeek format")
                    }
                    const [startStr] = range.split(" - ")
                    if (!startStr) {
                      throw new Error("Invalid week range format")
                    }
                    const monthsAbbr: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
                    const [monAbbr, dayStr] = startStr.split(" ")
                    if (!monAbbr || !dayStr || !(monAbbr in monthsAbbr)) {
                      throw new Error("Invalid start date format")
                    }
                    const monday = new Date(Number.parseInt(yearStr), monthsAbbr[monAbbr], Number.parseInt(dayStr))
                    if (isNaN(monday.getTime())) {
                      throw new Error("Invalid date created")
                    }
                    monday.setDate(monday.getDate() - 7)
                    const friday = new Date(monday)
                    friday.setDate(monday.getDate() + 4)
                    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                    const wk = `${months[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${months[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                    setCurrentWeek(wk)
                    updateWeekDays(wk)
                    await loadWeekEntries(monday)
                    // Check if this week matches current week
                    const matchesCurrentWeek = checkIfWeekMatchesCurrentWeek(monday)
                    setIsGoToCurrentTSDisabled(matchesCurrentWeek)
                  } catch (e) {
                    console.error("Failed to navigate to previous week", e)
                    setWarnMessage("Failed to navigate to previous week. Please try again.")
                    setShowWarnToast(true)
                    setTimeout(() => setShowWarnToast(false), 3000)
                  }
                }}
              >
                &lt;
              </Button>
              <Button
                variant="outline"
                className="font-medium bg-white text-sm"
                onClick={() => setIsWeekSelectorOpen(true)}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {currentWeek || "Select Week"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="font-medium bg-white"
                disabled={isLoadingWeek || isGoToCurrentTSDisabled}
                onClick={async () => {
                  // Navigate to next week
                  if (!currentWeek) {
                    setWarnMessage("No week selected. Please select a week first.")
                    setShowWarnToast(true)
                    setTimeout(() => setShowWarnToast(false), 3000)
                    return
                  }
                  if (isLoadingWeek) return // Prevent multiple clicks
                  
                  try {
                    const [range, yearStr] = currentWeek.split(", ")
                    if (!range || !yearStr) {
                      throw new Error("Invalid currentWeek format")
                    }
                    const [startStr] = range.split(" - ")
                    if (!startStr) {
                      throw new Error("Invalid week range format")
                    }
                    const monthsAbbr: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
                    const [monAbbr, dayStr] = startStr.split(" ")
                    if (!monAbbr || !dayStr || !(monAbbr in monthsAbbr)) {
                      throw new Error("Invalid start date format")
                    }
                    const monday = new Date(Number.parseInt(yearStr), monthsAbbr[monAbbr], Number.parseInt(dayStr))
                    if (isNaN(monday.getTime())) {
                      throw new Error("Invalid date created")
                    }
                    monday.setDate(monday.getDate() + 7)
                    const friday = new Date(monday)
                    friday.setDate(monday.getDate() + 4)
                    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                    const wk = `${months[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${months[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                    setCurrentWeek(wk)
                    updateWeekDays(wk)
                    await loadWeekEntries(monday)
                    // Check if this week matches current week
                    const matchesCurrentWeek = checkIfWeekMatchesCurrentWeek(monday)
                    setIsGoToCurrentTSDisabled(matchesCurrentWeek)
                  } catch (e) {
                    console.error("Failed to navigate to next week", e)
                    setWarnMessage("Failed to navigate to next week. Please try again.")
                    setShowWarnToast(true)
                    setTimeout(() => setShowWarnToast(false), 3000)
                  }
                }}
              >
                &gt;
              </Button>
                <Button
                  variant="outline"
                  className="font-medium bg-white text-sm"
                  disabled={isGoToCurrentTSDisabled}
                  onClick={async () => {
                    // Use stored currentWeekStartDate/currentWeekEndDate from when v1 was last called (e.g. loadCurrentTimesheet).
                    // No v1 call here: GetTSbyWeek (loadWeekEntries) uses that stored Monday.
                    if (!v1CurrentWeekStartDate || !v1CurrentWeekEndDate) {
                      await loadCurrentTimesheet()
                      return
                    }
                    setIsGoToCurrentTSDisabled(true)
                    try {
                      const monday = new Date(v1CurrentWeekStartDate)
                      monday.setHours(0, 0, 0, 0)
                      const friday = new Date(v1CurrentWeekEndDate)
                      const monthsAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
                      const wk = `${monthsAbbr[monday.getMonth()]} ${monday.getDate().toString().padStart(2,'0')} - ${monthsAbbr[friday.getMonth()]} ${friday.getDate().toString().padStart(2,'0')}, ${monday.getFullYear()}`
                      setCurrentWeek(wk)
                      setWeekDays(buildWeekDaysFromMonday(monday))
                      updateWeekDays(wk, true)
                      await loadWeekEntries(monday)
                      setIsViewingSpecificWeek(true)
                    } catch (e) {
                      console.error("Failed to go to current timesheet", e)
                      setWarnMessage("Failed to load current timesheet. Please try again.")
                      setShowWarnToast(true)
                      setTimeout(() => setShowWarnToast(false), 3000)
                      setIsGoToCurrentTSDisabled(false)
                    }
                  }}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  {isLoadingWeek ? "Loading..." : "Go To Current TS"}
                </Button>
                <Button
                  variant="outline"
                  className="font-medium bg-white text-sm"
                  disabled={!isViewingCurrentTSWeek() || !timesheetId}
                  onClick={async () => {
                    // Copy Previous Week copies data FROM previous week TO current TS
                    // So button should only be enabled when viewing CURRENT TS week (where data goes TO)
                    // Disabled when viewing OLD TS week (where data comes FROM)
                    if (!timesheetId) {
                      setWarnMessage("Please create a timesheet first")
                      setShowWarnToast(true)
                      setTimeout(() => setShowWarnToast(false), 3000)
                      return
                    }
                    
                    // Only allow copy when viewing current TS week (matches v1 currentWeekStartDate/EndDate)
                    if (!isViewingCurrentTSWeek()) {
                      setWarnMessage("Copy Previous Week is only available when viewing current timesheet")
                      setShowWarnToast(true)
                      setTimeout(() => setShowWarnToast(false), 3000)
                      return
                    }
                    
                    try {
                      setIsCopyingPreviousWeek(true)
                      const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/copy-previous-week`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          timesheetID: timesheetId
                        })
                      })
                      
                      if (!res.ok) {
                        // If API returns error, it might mean no previous week exists
                        if (res.status === 404 || res.status === 400) {
                          setWarnMessage("No previous timesheet available")
                          setShowWarnToast(true)
                          setTimeout(() => setShowWarnToast(false), 3000)
                          setIsCopyingPreviousWeek(false)
                          return
                        }
                        throw new Error(`${res.status}`)
                      }
                      
                      const data: any[] = await res.json()
                      
                      if (!data || data.length === 0) {
                        setWarnMessage("No previous timesheet available")
                        setShowWarnToast(true)
                        setTimeout(() => setShowWarnToast(false), 3000)
                        setIsCopyingPreviousWeek(false)
                        return
                      }
                      
                      // Aggregate duplicate entries (same engagement, task, and day)
                      const aggregatedData = aggregateDuplicateEntries(data)
                      
                      // Map API data to weeklyEntries structure
                      const grouped: { [key: string]: WeeklyTaskEntry } = {}
                      
                      aggregatedData.forEach((d) => {
                        const dateObj = new Date(d.date)
                        const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()]
                        const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][dateObj.getMonth()]
                        const label = `${weekday}, ${monthName} ${dateObj.getDate()}`
                        
                        const engagementID = d.engagementID ?? d.EngagementID ?? 0
                        const taskID = d.taskID ?? d.TaskID ?? 0
                        const key = `${engagementID}-${taskID}`
                        
                        if (!grouped[key]) {
                          grouped[key] = {
                            id: key,
                            engagementID: engagementID,
                            engagementName: "", // Will be populated from existing entries or API
                            taskID: taskID,
                            taskName: "", // Will be populated from existing entries or API
                            times: {}
                          }
                        }
                        
                        grouped[key].times[label] = {
                          hours: d.hours ?? d.Hours ?? 0,
                          minutes: d.minutes ?? d.Minutes ?? 0,
                          comment: decodeHtmlEntities(d.comment ?? d.Comment ?? ""),
                          lineID: d.lineID ?? d.LineID ?? undefined,
                          duplicateLineIDs: (d as any).duplicateLineIDs ?? undefined,
                        }
                      })
                      
                      // Merge with existing entries to preserve engagement/task names
                      setWeeklyEntries(prev => {
                        const updated = Object.values(grouped).map(newEntry => {
                          // Try to find existing entry with same engagementID and taskID
                          const existing = prev.find(e => e.engagementID === newEntry.engagementID && e.taskID === newEntry.taskID)
                          if (existing) {
                            // Merge times, keeping existing names
                            return {
                              ...newEntry,
                              engagementName: existing.engagementName,
                              taskName: existing.taskName,
                              times: { ...existing.times, ...newEntry.times }
                            }
                          }
                          return newEntry
                        })
                        
                        // Add entries that don't exist in new data but exist in prev
                        const newKeys = new Set(Object.keys(grouped))
                        prev.forEach(existing => {
                          const key = `${existing.engagementID}-${existing.taskID}`
                          if (!newKeys.has(key)) {
                            updated.push(existing)
                          }
                        })
                        
                        return updated
                      })
                      
                      // Update selected engagements
                      const engIds = new Set(Object.values(grouped).map(e => e.engagementID))
                      setSelectedEngagements(prev => {
                        const combined = new Set(prev)
                        engIds.forEach(id => combined.add(id))
                        return combined
                      })
                      
                      // Auto-expand all engagements that have entries
                      setExpandedEngagements(prev => {
                        const combined = new Set(prev)
                        engIds.forEach(id => combined.add(id))
                        return combined
                      })
                      
                      // Reload current week data to get engagement and task names
                      await reloadCurrentData()
                      
                      setSuccessMessage("Previous week data copied successfully")
                      setShowSuccessToast(true)
                      setTimeout(() => setShowSuccessToast(false), 3000)
                    } catch (e) {
                      console.error("Failed to copy previous week", e)
                      setWarnMessage("Failed to copy previous week. Please try again.")
                      setShowWarnToast(true)
                      setTimeout(() => setShowWarnToast(false), 3000)
                    } finally {
                      setIsCopyingPreviousWeek(false)
                    }
                  }}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  {isCopyingPreviousWeek ? "Copying..." : "Copy Previous Week"}
                </Button>
              <div className="flex items-center gap-2">
                <label htmlFor="weekend-toggle" className="text-sm text-gray-600 cursor-pointer">
                  Show Weekend
                </label>
                <Switch
                  id="weekend-toggle"
                  checked={showWeekend}
                  onCheckedChange={setShowWeekend}
                />
              </div>
              <div className="text-sm ml-auto flex items-center gap-2">
                <span className={"text-gray-600"}>Submitted Hours: </span>
                <span className="font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">{formatWeeklyHours(weekTotalHours)}</span>
                <span className={`ml-2 text-gray-600`}>Required: 40h</span>
                {isAutosaving && (
                  <span className="text-xs text-blue-600 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving...
                  </span>
                )}
              </div>
              <Select
                value=""
                open={engagementSelectOpen}
                disabled={timesheetStatus === 2 || timesheetStatus === 3}
                onOpenChange={(open) => {
                  if (!open) {
                    setEngagementSelectOpen(false)
                    return
                  }
                  // Editable only when draft or rejected
                  if (timesheetStatus !== 1 && timesheetStatus !== 4 && timesheetStatus !== null) return
                  if (weekDays.length === 0) return

                  const monday = weekDays.find(d => d.day === "Mon")
                  if (!monday?.iso) return

                  // Always open and fetch: engagementOptions may be stale (e.g. after "Go to Current TS"
                  // loadWeekEntries only sets options from timesheet lines, so empty week leaves options
                  // from previous week or "all selected" with no fetch for full list).
                  setEngagementSelectOpen(true)
                  setIsLoadingEngagements(true)
                  const { startDate, endDate } = getWeekStartAndEndDates(monday.iso)
                  const startDateEncoded = encodeURIComponent(startDate)
                  const endDateEncoded = encodeURIComponent(endDate)
                  fetch(process.env.REACT_APP_API_URL + `/api/Engagement/GetEngagementsByUserAndWeek/${startDateEncoded}/${endDateEncoded}`, { credentials: "include" })
                    .then(r => r.ok ? r.json() : Promise.reject(r.status))
                    .then((engData: { validation: { status: number; message: string }; engagements: any[] }) => {
                      if (engData.engagements && Array.isArray(engData.engagements)) {
                        const apiEngagements = engData.engagements.map(x => ({
                          engagementID: x.engagementID,
                          title: x.title ?? String(x.engagementID),
                          startDate: x.startDate,
                          endDate: x.endDate
                        }))
                        const timesheetEngagements = new Map<number, { engagementID: number; title: string; startDate?: string; endDate?: string }>()
                        weeklyEntries.forEach(entry => {
                          if (entry.engagementID && entry.engagementName && !timesheetEngagements.has(entry.engagementID)) {
                            timesheetEngagements.set(entry.engagementID, {
                              engagementID: entry.engagementID,
                              title: entry.engagementName
                            })
                          }
                        })
                        const apiEngIds = new Set(apiEngagements.map(e => e.engagementID))
                        const toAdd = Array.from(timesheetEngagements.values()).filter(e => !apiEngIds.has(e.engagementID))
                        const merged = [...apiEngagements, ...toAdd]
                        setEngagementOptions(merged)
                        const available = merged.filter(e => !selectedEngagements.has(e.engagementID))
                        if (available.length === 0) {
                          setEngagementSelectOpen(false)
                          const hasAny = merged.length > 0
                          toast({
                            title: hasAny ? "All engagements added" : "No engagements available",
                            description: hasAny
                              ? "All your engagements for this week are already added."
                              : "You have no active engagement right now.",
                            duration: 4000,
                            className: "border border-amber-200",
                          })
                        }
                      } else {
                        // API returned no or invalid engagements list
                        setEngagementSelectOpen(false)
                        toast({
                          title: "No engagements available",
                          description: "You have no active engagement right now.",
                          duration: 4000,
                          className: "border border-amber-200",
                        })
                      }
                    })
                    .catch(() => {
                      console.error("Failed to load engagements")
                      toast({
                        title: "Could not load engagements",
                        description: "Please check your connection and try again.",
                        duration: 4000,
                        className: "border border-red-200",
                      })
                      setEngagementSelectOpen(false)
                    })
                    .finally(() => setIsLoadingEngagements(false))
                }}
                onValueChange={(value) => {
                  setEngagementSelectOpen(false)
                  if (timesheetStatus === 2 || timesheetStatus === 3) return
                  const engId = Number.parseInt(value)
                  let eng = engagementOptions.find(e => e.engagementID === engId)
                  
                  // If engagement not found in options, try to get it from the select item
                  if (!eng) {
                    // This shouldn't happen if dropdown loaded correctly, but add as fallback
                    console.warn("Engagement not found in options, adding placeholder")
                    eng = { engagementID: engId, title: `Engagement ${engId}` }
                    setEngagementOptions(prev => {
                      if (!prev.find(e => e.engagementID === engId)) {
                        return [...prev, eng!]
                      }
                      return prev
                    })
                  }
                  
                  // Add engagement to selected engagements
                  setSelectedEngagements(prev => {
                    const newSet = new Set(prev)
                    newSet.add(engId)
                    return newSet
                  })
                  
                  // Expand this engagement automatically
                  const newExpanded = new Set(expandedEngagements)
                  newExpanded.add(engId)
                  setExpandedEngagements(newExpanded)
                  
                  // Automatically add a task row with dropdown open
                  setAddingEngagementId(engId)
                  setTaskSelectOpen(true)
                  
                  // Load tasks for this engagement via API
                  setIsLoadingTasks(true)
                  fetch(process.env.REACT_APP_API_URL + `/api/Engagement/${engId}`, { credentials: "include" })
                    .then(r => {
                      if (!r.ok) {
                        throw new Error(`API returned status ${r.status}`)
                      }
                      return r.json()
                    })
                    .then((list: any[]) => {
                      if (Array.isArray(list)) {
                        setTaskOptions(list.map(t => ({ taskID: t.taskID, taskName: t.taskName ?? String(t.taskID) })))
                      } else {
                        console.warn("Tasks API returned non-array:", list)
                        setTaskOptions([])
                      }
                    })
                    .catch((error) => {
                      console.error("Failed to load tasks:", error)
                      setTaskOptions([])
                      toast({
                        title: "Failed to load tasks",
                        description: "Unable to load tasks for this engagement. Please try again.",
                        duration: 3000,
                        className: "border border-yellow-300",
                      })
                    })
                    .finally(() => setIsLoadingTasks(false))
                  
                  // Reset the select value by forcing re-render
                  // The Select component should reset when value is empty string
                }}
              >
                <SelectTrigger className="bg-white w-[200px]" disabled={timesheetStatus === 2 || timesheetStatus === 3}>
                  <SelectValue placeholder="+ Add Engagement" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[240px] overflow-y-auto z-[60]" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  {isLoadingEngagements ? (
                    <div className="py-4 px-3 text-sm text-gray-500 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading engagements...
                    </div>
                  ) : (() => {
                    const available = engagementOptions.filter(e => !selectedEngagements.has(e.engagementID))
                    if (available.length === 0) {
                      return (
                        <div className="py-4 px-3 text-sm text-gray-500 text-center">
                          You have no active engagement right now.
                        </div>
                      )
                    }
                    return available.map((e) => (
                      <SelectItem key={e.engagementID} value={e.engagementID.toString()} className="hover:bg-gray-100">
                        {e.title}
                      </SelectItem>
                    ))
                  })()}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Loading timesheet...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <div className="min-w-[900px]">
                  {/* Table Header */}
                  <div className="grid gap-2 text-sm font-semibold border-b-2 pb-2 text-white bg-blue-400 rounded-t-lg px-4 py-3"
                       style={{ gridTemplateColumns: `2fr repeat(${weekDays.filter(d => !d.isWeekend || showWeekend).length}, 1fr) 1.2fr` }}>
                    <div className="flex items-center">ENGAGEMENT / TASK</div>
                    {weekDays.filter(d => !d.isWeekend || showWeekend).map((day) => (
                      <div key={day.day} className="text-center flex items-center justify-center">
                        {day.day.toUpperCase()} {day.date}
                      </div>
                    ))}
                    <div className="text-center flex items-center justify-center">TOTAL</div>
                  </div>

                  {/* Engagement/Task Rows */}
                  {selectedEngagements.size === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No entries for this week</p>
                      <p className="text-sm">Select an engagement to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {Array.from(selectedEngagements).map((engId) => {
                        const eng = engagementOptions.find(e => e.engagementID === engId)
                        if (!eng) return null
                        
                        // Get tasks for this engagement
                        const engTasks = weeklyEntries.filter(e => e.engagementID === engId)
                        const engData = {
                          engagementID: engId,
                          engagementName: eng.title,
                          tasks: engTasks
                        }
                        const isExpanded = expandedEngagements.has(engData.engagementID)
                        const engTotal = engData.tasks.reduce((sum, task) => {
                          const taskTotal = Object.values(task.times).reduce((tSum, time) => tSum + time.hours * 60 + time.minutes, 0)
                          return sum + taskTotal
                        }, 0)
                        const engHours = Math.floor(engTotal / 60)
                        const engMins = engTotal % 60

                        return (
                          <div key={engId} className="border-b border-gray-200">
                            {/* Engagement Row */}
                            <div className="grid gap-2 items-center py-2 px-4 hover:bg-gray-50"
                                 style={{ gridTemplateColumns: `2fr repeat(${weekDays.filter(d => !d.isWeekend || showWeekend).length}, 1fr) 1.2fr` }}>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const newExpanded = new Set(expandedEngagements)
                                    if (isExpanded) {
                                      newExpanded.delete(engData.engagementID)
                                    } else {
                                      newExpanded.add(engData.engagementID)
                                    }
                                    setExpandedEngagements(newExpanded)
                                  }}
                                  className="p-1 hover:bg-gray-200 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-600" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                  )}
                                </button>
                                <span className="font-medium text-gray-800">{engData.engagementName}</span>
                                {isExpanded && addingEngagementId !== engData.engagementID && (timesheetStatus === 1 || timesheetStatus === 4 || timesheetStatus === null) && (
                                  <button
                                    onClick={() => {
                                      setAddingEngagementId(engData.engagementID)
                                      setTaskSelectOpen(true)
                                      setIsLoadingTasks(true)
                                      fetch(process.env.REACT_APP_API_URL + `/api/Engagement/${engData.engagementID}`, { credentials: "include" })
                                        .then(r => {
                                          if (!r.ok) {
                                            throw new Error(`API returned status ${r.status}`)
                                          }
                                          return r.json()
                                        })
                                        .then((list: any[]) => {
                                          if (Array.isArray(list)) {
                                            setTaskOptions(list.map(t => ({ taskID: t.taskID, taskName: t.taskName ?? String(t.taskID) })))
                                          } else {
                                            console.warn("Tasks API returned non-array:", list)
                                            setTaskOptions([])
                                          }
                                        })
                                        .catch((error) => {
                                          console.error("Failed to load tasks:", error)
                                          setTaskOptions([])
                                          toast({
                                            title: "Failed to load tasks",
                                            description: "Unable to load tasks. Please try again.",
                                            duration: 3000,
                                            className: "border border-yellow-300",
                                          })
                                        })
                                        .finally(() => setIsLoadingTasks(false))
                                    }}
                                    className="ml-2 p-1 border-2 border-blue-500 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors flex items-center justify-center"
                                    title="Add Task"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              {weekDays.filter(d => !d.isWeekend || showWeekend).map((day) => (
                                <div key={day.day} className="text-center text-gray-600">-</div>
                              ))}
                              <div className="text-center font-medium text-gray-800 flex items-center justify-center gap-2">
                                <span>{engHours > 0 || engMins > 0 ? `${engHours}h ${engMins}m` : '-'}</span>
                                <button
                                  onClick={() => {
                                    setItemToDelete({ type: 'engagement', id: engId, name: engData.engagementName, engagementID: engId })
                                    setDeleteConfirmOpen(true)
                                  }}
                                  disabled={timesheetStatus === 2 || timesheetStatus === 3}
                                  className={`text-red-600 hover:text-red-700 ${(timesheetStatus === 2 || timesheetStatus === 3) ? "opacity-50 cursor-not-allowed" : ""}`}
                                  title="Remove engagement"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {/* Task Rows */}
                            {isExpanded && engData.tasks.map((task) => {
                              const taskTotal = Object.values(task.times).reduce((sum, time) => sum + time.hours * 60 + time.minutes, 0)
                              const taskHours = Math.floor(taskTotal / 60)
                              const taskMins = taskTotal % 60

                              return (
                                <div key={task.id} className="grid gap-2 items-center py-2 px-4 bg-gray-50 border-t border-gray-100"
                                     style={{ gridTemplateColumns: `2fr repeat(${weekDays.filter(d => !d.isWeekend || showWeekend).length}, 1fr) 1.2fr` }}>
                                  <div className="flex items-center gap-2 pl-10">
                                    <span className="text-gray-700">{task.taskName}</span>
                                  </div>
                                  {weekDays.filter(d => !d.isWeekend || showWeekend).map((day) => {
                                    const dayKey = day.full
                                    const timeEntry = task.times[dayKey] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
                                    const cellKey = `${task.id}-${dayKey}`
                                    const isDateDisabled = day.iso ? !isDateWithinEngagementRange(task.engagementID, day.iso) : false
                                    const isFuture = day.iso ? isFutureDate(day.iso) : false
                                    const isDisabled = timesheetStatus === 2 || timesheetStatus === 3 || isDateDisabled || isFuture
                                    const hasFailedSave = failedCells.has(cellKey)
                                    const canEdit = timesheetStatus !== 2 && timesheetStatus !== 3 && !isDateDisabled && !isFuture
                                    const canViewComments = timesheetStatus === 2 || timesheetStatus === 3 || canEdit

                                    return (
                                      <div key={day.day} className="flex items-center justify-center gap-1">
                                        <div className={`flex items-center gap-1 border rounded px-2 py-1 bg-white min-w-[80px] justify-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""} ${hasFailedSave ? "border-red-500 border-2" : ""}`}>
                                          <input
                                            type="number"
                                            min="0"
                                            max="12"
                                            placeholder="0"
                                            value={timeEntry.hours || ""}
                                            onFocus={() => {
                                              // Store initial values when cell is focused
                                              setFocusedCellValues(prev => ({
                                                ...prev,
                                                [cellKey]: {
                                                  hours: timeEntry.hours || 0,
                                                  minutes: timeEntry.minutes || 0
                                                }
                                              }))
                                            }}
                                            onChange={(e) => {
                                              const hours = Number.parseInt(e.target.value) || 0
                                              const updatedEntry = { ...task, times: { ...task.times, [dayKey]: { ...timeEntry, hours } } }
                                              setWeeklyEntries(prev => prev.map(e => 
                                                e.id === task.id 
                                                  ? updatedEntry
                                                  : e
                                              ))
                                              // Remove red border when user starts changing the faulty cell
                                              if (failedCells.has(cellKey)) {
                                                setFailedCells(prev => {
                                                  const newSet = new Set(prev)
                                                  newSet.delete(cellKey)
                                                  return newSet
                                                })
                                              }
                                              triggerAutosave(updatedEntry, dayKey)
                                            }}
                                            onBlur={() => {
                                              // Don't save on blur if cell has a failed save (prevents duplicate failed API calls)
                                              if (failedCells.has(cellKey)) {
                                                // Clear the focused value tracking
                                                setFocusedCellValues(prev => {
                                                  const newValues = { ...prev }
                                                  delete newValues[cellKey]
                                                  return newValues
                                                })
                                                return
                                              }
                                              
                                              // Only save on blur if value changed from when cell was focused
                                              const currentEntry = weeklyEntries.find(e => e.id === task.id)
                                              if (!currentEntry) return
                                              
                                              const currentTime = currentEntry.times[dayKey] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
                                              const initialValues = focusedCellValues[cellKey]
                                              
                                              // Check if value changed from initial focus
                                              const valueChanged = initialValues 
                                                ? (currentTime.hours !== initialValues.hours || currentTime.minutes !== initialValues.minutes)
                                                : false
                                              
                                              // Only save if value actually changed (prevents unnecessary saves when just clicking and clicking away)
                                              if (valueChanged) {
                                                triggerAutosave(currentEntry, dayKey, true)
                                              }
                                              
                                              // Clear the focused value tracking
                                              setFocusedCellValues(prev => {
                                                const newValues = { ...prev }
                                                delete newValues[cellKey]
                                                return newValues
                                              })
                                            }}
                                            disabled={isDisabled}
                                            className="w-8 text-center border-none outline-none text-sm disabled:cursor-not-allowed"
                                          />
                                          <span className="text-gray-400">:</span>
                                          <input
                                            type="number"
                                            min="0"
                                            max="59"
                                            step="5"
                                            placeholder="0"
                                            value={timeEntry.minutes || ""}
                                            onFocus={() => {
                                              // Store initial values when cell is focused (if not already stored)
                                              if (!focusedCellValues[cellKey]) {
                                                setFocusedCellValues(prev => ({
                                                  ...prev,
                                                  [cellKey]: {
                                                    hours: timeEntry.hours || 0,
                                                    minutes: timeEntry.minutes || 0
                                                  }
                                                }))
                                              }
                                            }}
                                            onChange={(e) => {
                                              const minutes = Number.parseInt(e.target.value) || 0
                                              const updatedEntry = { ...task, times: { ...task.times, [dayKey]: { ...timeEntry, minutes } } }
                                              setWeeklyEntries(prev => prev.map(e => 
                                                e.id === task.id 
                                                  ? updatedEntry
                                                  : e
                                              ))
                                              // Remove red border when user starts changing the faulty cell
                                              if (failedCells.has(cellKey)) {
                                                setFailedCells(prev => {
                                                  const newSet = new Set(prev)
                                                  newSet.delete(cellKey)
                                                  return newSet
                                                })
                                              }
                                              triggerAutosave(updatedEntry, dayKey)
                                            }}
                                            onBlur={() => {
                                              // Don't save on blur if cell has a failed save (prevents duplicate failed API calls)
                                              if (failedCells.has(cellKey)) {
                                                // Clear the focused value tracking
                                                setFocusedCellValues(prev => {
                                                  const newValues = { ...prev }
                                                  delete newValues[cellKey]
                                                  return newValues
                                                })
                                                return
                                              }
                                              
                                              // Only save on blur if value changed from when cell was focused
                                              const currentEntry = weeklyEntries.find(e => e.id === task.id)
                                              if (!currentEntry) return
                                              
                                              const currentTime = currentEntry.times[dayKey] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
                                              const initialValues = focusedCellValues[cellKey]
                                              
                                              // Check if value changed from initial focus
                                              const valueChanged = initialValues 
                                                ? (currentTime.hours !== initialValues.hours || currentTime.minutes !== initialValues.minutes)
                                                : false
                                              
                                              // Only save if value actually changed (prevents unnecessary saves when just clicking and clicking away)
                                              if (valueChanged) {
                                                triggerAutosave(currentEntry, dayKey, true)
                                              }
                                              
                                              // Clear the focused value tracking
                                              setFocusedCellValues(prev => {
                                                const newValues = { ...prev }
                                                delete newValues[cellKey]
                                                return newValues
                                              })
                                            }}
                                            disabled={isDisabled}
                                            className="w-8 text-center border-none outline-none text-sm disabled:cursor-not-allowed"
                                          />
                                        </div>
                                        <Popover open={commentPopovers[cellKey] || false} onOpenChange={(open) => {
                                          // Allow opening if user can view comments (even if can't edit)
                                          if (!canViewComments) return
                                          setCommentPopovers(prev => ({ ...prev, [cellKey]: open }))
                                        }}>
                                          <PopoverTrigger asChild>
                                            <button 
                                              disabled={!canViewComments}
                                              className={`p-1 hover:bg-gray-200 rounded ${!canViewComments ? "opacity-50 cursor-not-allowed" : ""}`}
                                            >
                                              <MessageSquare className={`w-4 h-4 ${timeEntry.comment ? "text-blue-600" : "text-gray-400"}`} />
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-80">
                                            <div className="space-y-2">
                                              <label className="text-sm font-medium">Comment {canEdit ? "(optional)" : ""}</label>
                                              <Textarea
                                                placeholder={canEdit ? "Add a comment..." : "No comment"}
                                                value={commentTexts[cellKey] !== undefined ? commentTexts[cellKey] : (timeEntry.comment || "")}
                                                onChange={(e) => {
                                                  if (canEdit) {
                                                    setCommentTexts(prev => ({ ...prev, [cellKey]: e.target.value }))
                                                  }
                                                }}
                                                disabled={!canEdit}
                                                readOnly={!canEdit}
                                                onBlur={() => {
                                                  // Only process if user can edit and actually interacted with the field
                                                  if (canEdit && commentTexts[cellKey] !== undefined) {
                                                    const comment = commentTexts[cellKey] || ""
                                                    const originalComment = timeEntry.comment || ""
                                                    
                                                    // Only update and save if comment actually changed
                                                    if (comment !== originalComment) {
                                                      const updatedEntry = { ...task, times: { ...task.times, [dayKey]: { ...timeEntry, comment } } }
                                                      setWeeklyEntries(prev => prev.map(e => 
                                                        e.id === task.id 
                                                          ? updatedEntry
                                                          : e
                                                      ))
                                                      triggerAutosave(updatedEntry, dayKey, true)
                                                    }
                                                  }
                                                  
                                                  // Clear the temporary comment text from state
                                                  setCommentTexts(prev => {
                                                    const newTexts = { ...prev }
                                                    delete newTexts[cellKey]
                                                    return newTexts
                                                  })
                                                }}
                                                rows={3}
                                                className="resize-none"
                                              />
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      </div>
                                    )
                                  })}
                                  <div className="text-center font-medium text-gray-800 flex items-center justify-center gap-2">
                                    <span>{taskHours > 0 || taskMins > 0 ? `${taskHours}h ${taskMins}m` : '-'}</span>
                                    <button
                                      onClick={() => {
                                        setItemToDelete({ type: 'task', id: task.id, name: task.taskName, engagementID: task.engagementID, taskID: task.taskID })
                                        setDeleteConfirmOpen(true)
                                      }}
                                      disabled={timesheetStatus === 2 || timesheetStatus === 3}
                                      className={`text-red-600 hover:text-red-700 ${(timesheetStatus === 2 || timesheetStatus === 3) ? "opacity-50 cursor-not-allowed" : ""}`}
                                      title="Remove task"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )
                            })}

                            {/* Add Task Dropdown Row */}
                            {isExpanded && addingEngagementId === engData.engagementID && (
                              <div className="grid gap-2 items-center py-2 px-4 bg-blue-50 border-t border-blue-200"
                                   style={{ gridTemplateColumns: `2fr repeat(${weekDays.filter(d => !d.isWeekend || showWeekend).length}, 1fr) 1.2fr` }}>
                                <div className="flex items-center gap-2 pl-10">
                                  <Select
                                    value={addingTaskId || ""}
                                    open={taskSelectOpen && addingEngagementId === engData.engagementID}
                                    onOpenChange={(open) => {
                                      setTaskSelectOpen(open)
                                      if (!open) {
                                        setAddingEngagementId(null)
                                        setAddingTaskId(null)
                                      }
                                    }}
                                    onValueChange={(value) => {
                                      const task = taskOptions.find(t => t.taskID.toString() === value)
                                      if (task) {
                                        // Check if task already exists for this engagement
                                        const taskExists = weeklyEntries.some(
                                          e => e.engagementID === engData.engagementID && e.taskID === task.taskID
                                        )
                                        if (taskExists) {
                                          setAddingTaskId(null)
                                          setAddingEngagementId(null)
                                          setTaskSelectOpen(false)
                                          return
                                        }
                                        
                                        const newTask: WeeklyTaskEntry = {
                                          id: `${engData.engagementID}-${task.taskID}-${Date.now()}`,
                                          engagementID: engData.engagementID,
                                          engagementName: engData.engagementName,
                                          taskID: task.taskID,
                                          taskName: task.taskName,
                                          times: {}
                                        }
                                        setWeeklyEntries(prev => [...prev, newTask])
                                        setAddingTaskId(null)
                                        setAddingEngagementId(null)
                                        setTaskSelectOpen(false)
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="bg-white w-full">
                                      <SelectValue placeholder={isLoadingTasks ? "Loading tasks..." : "Select Task"} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white max-h-[240px] overflow-y-auto" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                      {isLoadingTasks ? (
                                        <div className="py-4 px-3 text-sm text-gray-500 flex items-center justify-center gap-2">
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                          Loading tasks...
                                        </div>
                                      ) : (() => {
                                        const availableTasks = taskOptions.filter(t => !weeklyEntries.some(
                                          e => e.engagementID === engData.engagementID && e.taskID === t.taskID
                                        ))
                                        if (availableTasks.length === 0) {
                                          return (
                                            <div className="py-4 px-3 text-sm text-gray-500 text-center">
                                              {taskOptions.length === 0
                                                ? "No tasks available for this engagement."
                                                : "All tasks for this engagement are already added."}
                                            </div>
                                          )
                                        }
                                        return availableTasks.map((t) => (
                                          <SelectItem key={t.taskID} value={t.taskID.toString()} className="hover:bg-gray-100">
                                            {t.taskName}
                                          </SelectItem>
                                        ))
                                      })()}
                                    </SelectContent>
                                  </Select>
                                  <button
                                    onClick={() => {
                                      setAddingEngagementId(null)
                                      setAddingTaskId(null)
                                      setTaskSelectOpen(false)
                                    }}
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                {weekDays.filter(d => !d.isWeekend || showWeekend).map(() => (
                                  <div key={Math.random()}></div>
                                ))}
                                <div></div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Totals Row */}
                  {selectedEngagements.size > 0 && (
                    <div className="grid gap-2 items-center py-3 px-4 bg-gray-100 border-t-2 border-gray-300 font-semibold"
                         style={{ gridTemplateColumns: `2fr repeat(${weekDays.filter(d => !d.isWeekend || showWeekend).length}, 1fr) 1.2fr` }}>
                      <div className="text-gray-800">TOTAL</div>
                      {weekDays.filter(d => !d.isWeekend || showWeekend).map((day) => {
                        const dayTotal = weeklyEntries.reduce((sum, entry) => {
                          const time = entry.times[day.full] || { hours: 0, minutes: 0, comment: "" }
                          return sum + time.hours * 60 + time.minutes
                        }, 0)
                        const dayHours = Math.floor(dayTotal / 60)
                        const dayMins = dayTotal % 60
                        return (
                          <div key={day.day} className="text-center text-gray-800">
                            {dayHours > 0 || dayMins > 0 ? `${dayHours}h ${dayMins}m` : '-'}
                          </div>
                        )
                      })}
                      <div className="text-center text-gray-800">
                        {formatWeeklyHours(weekTotalHours)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Section */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  <span>TS Status: </span>
                  {timesheetStatus !== null && (
                    <Badge
                      variant="outline"
                      className={`text-xs ml-2 ${
                        timesheetStatus === 4 
                          ? "bg-red-100 text-red-700 border-red-300"
                          : timesheetStatus === 3
                          ? "bg-green-100 text-green-700 border-green-300"
                          : timesheetStatus === 2
                          ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                          : "bg-gray-100 text-gray-700 border-gray-300"
                      }`}
                    >
                      {timesheetStatus === 4 ? "Rejected" : 
                       timesheetStatus === 3 ? "Approved" : 
                       timesheetStatus === 2 ? "Submitted" : "Pending"}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      if (!timesheetId) return
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/getvptimesheetcomments?timesheetid=${timesheetId}`, { credentials: "include" })
                        if (res.ok) {
                          const data = await res.json()
                          setCommentsData(Array.isArray(data) ? data : [])
                          setIsCommentsOpen(true)
                        } else {
                          setCommentsData([])
                          setIsCommentsOpen(true)
                          toast({
                            title: "Could not load comments",
                            description: "Please try again later.",
                            duration: 3000,
                            className: "border border-amber-200",
                          })
                        }
                      } catch (e) {
                        console.error("Failed to load comments", e)
                        setCommentsData([])
                        setIsCommentsOpen(true)
                        toast({
                          title: "Could not load comments",
                          description: "Please check your connection and try again.",
                          duration: 3000,
                          className: "border border-red-200",
                        })
                      }
                    }}
                    disabled={!timesheetId || timesheetStatus === 1 || timesheetStatus === null}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Comments for TS
                  </Button>
                  <Button
                    onClick={handleSubmitTimesheet}
                    disabled={!weekdaysFilled || weekTotalHoursExcludingWeekends < 40 || timesheetStatus === 2 || timesheetStatus === 3 || isSubmitting}
                    className={`${weekdaysFilled && weekTotalHoursExcludingWeekends >= 40 && !isSubmitting && timesheetStatus !== 2 && timesheetStatus !== 3
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit for Approval
                      </>
                    )}
                  </Button>
                </div>
              </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <Dialog open={isSubmitOpen} onOpenChange={setIsSubmitOpen}>
          <DialogContent className={`max-w-sm bg-white`}>
            <DialogHeader>
              <DialogTitle className={'text-gray-900'}>Submit Confirmation</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">Are you sure you want to submit?</p>
              <div>
                <div className="flex items-center justify-between mb-1">
                <label className="block text-sm text-gray-600">Comments</label>
                  <span className="text-xs text-gray-500">
                  {submitComment.length}/1000
                </span>
              </div>
              <Textarea 
                value={submitComment} 
                onChange={(e) => setSubmitComment(e.target.value)} 
                placeholder="Optional comments"
                maxLength={1000}
              />
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsSubmitOpen(false)} 
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={doSubmitTimesheet} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {(showSuccessToast || showWarnToast) && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              backgroundColor: "white",
              color: showWarnToast ? "#92400e" : "black",
              padding: "12px 20px",
              borderRadius: "8px",
              boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
              zIndex: 1000,
              border: showWarnToast ? "2px solid #fbbf24" : "2px solid #10b981",
            }}
          >
            {showWarnToast ? warnMessage : successMessage}
          </div>
        )}

        <BulkEntryModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          onSubmit={handleBulkSubmit}
        />

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className={`max-w-sm bg-white`}>
            <DialogHeader>
              <DialogTitle className={'text-gray-900'}>Confirm Delete</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
                {itemToDelete?.type === 'engagement' && ' This will also delete all associated tasks.'}
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDeleteConfirmOpen(false)
                    setItemToDelete(null)
                  }} 
                  className="flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!itemToDelete || !timesheetId) {
                      setDeleteConfirmOpen(false)
                      setItemToDelete(null)
                      return
                    }

                    setIsDeleting(true)

                    // Helper: only call delete API when there is at least one saved line (lineID from backend)
                    const hasAnySavedLine = (): boolean => {
                      const engagementID = itemToDelete.engagementID || itemToDelete.id as number
                      
                      if (itemToDelete.type === 'task') {
                        const taskEntry = weeklyEntries.find(e => e.id === itemToDelete.id)
                        if (!taskEntry) return false
                        return weekDays.some(day => {
                          const timeEntry = taskEntry.times[day.full] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
                          return timeEntry.lineID != null
                        })
                      } else {
                        const engagementTasks = weeklyEntries.filter(e => e.engagementID === engagementID)
                        if (engagementTasks.length === 0) return false
                        return engagementTasks.some(task => {
                          return weekDays.some(day => {
                            const timeEntry = task.times[day.full] || { hours: 0, minutes: 0, comment: "", lineID: undefined }
                            return timeEntry.lineID != null
                          })
                        })
                      }
                    }

                    const hasSavedLines = hasAnySavedLine()

                    try {
                      // Only make API call if there are saved lines (lineIDs) on the server
                      if (hasSavedLines) {
                        const engagementID = itemToDelete.engagementID || itemToDelete.id as number
                        let url = `${process.env.REACT_APP_API_URL}/api/timesheet/${timesheetId}/${engagementID}`
                        
                        // Add taskId as query parameter if deleting a task
                        if (itemToDelete.type === 'task' && itemToDelete.taskID) {
                          url += `?taskId=${itemToDelete.taskID}`
                        }

                        const res = await fetch(url, {
                          method: "DELETE",
                          credentials: "include",
                        })

                        if (!res.ok) throw new Error(`${res.status}`)
                      }

                      // Update UI after deletion (whether API was called or not)
                      const engagementID = itemToDelete.engagementID || itemToDelete.id as number
                      if (itemToDelete.type === 'engagement') {
                        setSelectedEngagements(prev => {
                          const newSet = new Set(prev)
                          newSet.delete(engagementID)
                          return newSet
                        })
                        setWeeklyEntries(prev => prev.filter(e => e.engagementID !== engagementID))
                      } else {
                        setWeeklyEntries(prev => prev.filter(e => e.id !== itemToDelete.id))
                      }

                      // Reload data to ensure consistency (only if API was called)
                      if (hasSavedLines) {
                        if (isViewingSpecificWeek) {
                          try {
                            const [range, yearStr] = currentWeek.split(", ")
                            const [startStr] = range.split(" - ")
                            const [monAbbr, dayStr] = startStr.split(" ")
                            const mm: { [k: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
                            const monday = new Date(Number.parseInt(yearStr), mm[monAbbr], Number.parseInt(dayStr))
                            await loadWeekEntries(monday)
                          } catch (e) {
                            console.error("Failed to reload week entries", e)
                          }
                        } else {
                          await loadCurrentTimesheet()
                        }
                      }
                    } catch (e) {
                      console.error("Failed to delete", e)
                      setWarnMessage("Failed to delete. Please try again.")
                      setShowWarnToast(true)
                      setTimeout(() => setShowWarnToast(false), 3000)
                    } finally {
                      setIsDeleting(false)
                      setDeleteConfirmOpen(false)
                      setItemToDelete(null)
                    }
                  }} 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <WeekSelector
          isOpen={isWeekSelectorOpen}
          onClose={() => setIsWeekSelectorOpen(false)}
          currentWeek={currentWeek}
          rejectedRanges={rejectedRanges}
          selectedDate={selectedDate}
          maxSelectableISO={v1CurrentEndISO ?? undefined}
          minSelectableISO={firstTimesheetStartISO ?? undefined}
          onWeekSelect={async (week) => {
            if (!week) {
              console.warn("Week selector returned empty week")
              return
            }
            if (isLoadingWeek) return // Prevent multiple selections
            
            setCurrentWeek(week)
            try {
              const [range, yearStr] = week.split(", ")
              if (!range || !yearStr) {
                throw new Error("Invalid week format")
              }
              const [startStr] = range.split(" - ")
              if (!startStr) {
                throw new Error("Invalid week range format")
              }
              const [monAbbr, dayStr] = startStr.split(" ")
              if (!monAbbr || !dayStr) {
                throw new Error("Invalid start date format")
              }
              const mm: { [k: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
              if (mm[monAbbr] === undefined) {
                throw new Error(`Invalid month abbreviation: ${monAbbr}`)
              }
              const monday = new Date(Number.parseInt(yearStr), mm[monAbbr], Number.parseInt(dayStr))
              if (isNaN(monday.getTime())) {
                throw new Error("Invalid date created")
              }
              await loadWeekEntries(monday)
              // Check if selected week matches current week
              const matchesCurrentWeek = checkIfWeekMatchesCurrentWeek(monday)
              setIsGoToCurrentTSDisabled(matchesCurrentWeek)
            } catch (e) {
              console.error("Failed to parse week label", e)
              setWarnMessage("Failed to load selected week. Please try again.")
              setShowWarnToast(true)
              setTimeout(() => setShowWarnToast(false), 3000)
            }
            setIsWeekSelectorOpen(false)
          }}
        />

        <CommentsModalEmp
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
          title={`Timesheet #${timesheetId ?? ''}`}
          comments={
            commentsData.map((c: any, idx: number) => ({
              id: idx + 1,
              user: {
                name: c.commentByUser ?? "",
                initials: (c.commentByUser?.split(' ').map((p: string) => p[0]).join('') ?? 'U').slice(0,2).toUpperCase(),
              },
              action: c.commentTypeText ?? "",
              timestamp: new Date(c.commentDate).toLocaleString(),
              commentText: c.commentText && c.commentText.trim().length > 0 ? c.commentText : undefined,
            }))
          }
        />
      </div>
    </TooltipProvider>
  )
}
